/**
 * ClipTranscriptBridge.tsx
 *
 * Wraps the full Transcript component with overridden React context providers
 * so it reads from the clip's editor stores instead of the global stores.
 *
 * This enables maximum code reuse: the Transcript component and all its
 * sub-components (TranscriptionText, _TranscriptionDiff, KaraokeLinesView,
 * TranscriptContextMenu, etc.) work without any modifications.
 *
 * Structure:
 *  - ClipTranscriptBridge (exported) — accepts nullable props from
 *    useClipPreviewRenderer; renders a loading state until both deps are ready.
 *  - ClipTranscriptBridgeLoaded (internal) — receives non-null deps so all
 *    hooks inside can use them without `!` assertions.
 */

import {
  trackCompositionTextEdited,
  trackReelSectionRenamed,
  trackReelTranscriptEditorRevealed,
  trackReelTranscriptSectionSeeked,
  trackReelTranscriptStructureShown,
} from '@src/analytics/EventTracker';
import { useCompositionQuickEditOpenedTracking } from '@src/analytics/hooks/useCompositionQuickEditOpenedTracking';
import { DubCoverageNotice } from '@src/components/aiVoices/DubCoverageNotice';
import { getDubDurationLimitNote } from '@src/components/aiVoices/dubDurationLimit';
import DubGenerateModal from '@src/components/aiVoices/DubGenerateModal';
import { DubLevelRow } from '@src/components/aiVoices/DubLevelRow';
import RedubLandedPulse from '@src/components/aiVoices/RedubLandedPulse';
import RedubSelectionPopover from '@src/components/aiVoices/RedubSelectionPopover';
import {
  type ReviewHandoff,
  reviewHandoffFrom,
  type ReviewHandoffProp,
  type ReviewHandoffRequest,
} from '@src/components/aiVoices/reviewHandoff';
import { useDubCoverage } from '@src/components/aiVoices/useDubCoverage';
import { useDubGenerateModalController } from '@src/components/aiVoices/useDubGenerateModalController';
import { useKeepOriginalMusic } from '@src/components/aiVoices/useKeepOriginalMusic';
import { useMusicSeparationController } from '@src/components/aiVoices/useMusicSeparationController';
import { useRedubSelectionController } from '@src/components/aiVoices/useRedubSelectionController';
import { useRemoveDub } from '@src/components/aiVoices/useRemoveDub';
import useLoadBrandKitFonts from '@src/components/BrandKit/utils/useLoadBrandKitFonts';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { usePlayerSettingsSlot } from '@src/components/common/playerSettingsSlot';
import { LoadingState } from '@src/components/common/Spinner';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { CompositionCanvasActionMenu } from '@src/components/content-components/CompositionCanvasActionMenu';
import { OverlayGraphicPickerModal } from '@src/components/content-components/OverlayGraphicPickerModal';
import { CompositionProvider } from '@src/components/sidepanel/CompositionContext';
import { buildAITextPopoverContext } from '@src/components/sidepanel/CompositionSettings/controls/aiTextPopoverContext';
import { useAITextGeneration } from '@src/components/sidepanel/CompositionSettings/controls/useAITextGeneration';
import { CaptionStalenessNotice } from '@src/components/transcript/CaptionStalenessNotice';
import {
  CompositionQuickEditPopover,
  formatHeaderLabelFromMarker,
} from '@src/components/transcript/CompositionQuickEditPopover';
import { placeLanguageControls } from '@src/components/transcript/placeLanguageControls';
import { PlayerAudioPickers } from '@src/components/transcript/PlayerAudioPickers';
import { ReelStructureSummaryLine } from '@src/components/transcript/reelStructure/ReelStructureHeadings';
import {
  ReelTranscriptStructureContext,
  type ReelTranscriptStructureView,
} from '@src/components/transcript/reelStructure/ReelTranscriptStructureContext';
import { useReelTranscriptCollapse } from '@src/components/transcript/reelStructure/useReelTranscriptCollapse';
import {
  copyReviewLinkToastAction,
  reviewLinkFromParams,
} from '@src/components/transcript/reviewLinkToastAction';
import { Transcript } from '@src/components/transcript/Transcript';
import { TranscriptOverflowExtrasProvider } from '@src/components/transcript/TranscriptOverflowExtras';
import { TranslationGenerateModal } from '@src/components/transcript/TranslationGenerateModal';
import { TranslationReviewRestoreVeil } from '@src/components/transcript/TranslationReviewRestoreVeil';
import {
  type ReviewMode,
  TranslationReviewWorkspace,
} from '@src/components/transcript/TranslationReviewWorkspace';
import {
  LANGUAGE_MENU_HELP_TOOLTIP_ID,
  TranslationToggle,
} from '@src/components/transcript/TranslationToggle';
import { useTranslationGenerateModalController } from '@src/components/transcript/useTranslationGenerateModalController';
import {
  notifyCaptionFontSubstitution,
  switchCaptionLanguage,
} from '@src/hooks/switchCaptionLanguage';
import useAddMediaToTimeline from '@src/hooks/useAddMediaToTimeline';
import { useLanguageRemovalFallback } from '@src/hooks/useLanguageRemovalFallback';
import { useMissingTranslationCheck } from '@src/hooks/useMissingTranslationCheck';
import { useReproduceCaptionsOnTranslationEdit } from '@src/hooks/useReproduceCaptionsOnTranslationEdit';
import { useTranslationStoreHydration } from '@src/hooks/useTranslationStoreHydration';
import { SaveClipFromPreviewService } from '@src/services/clip/SaveClipFromPreviewService';
import { clipProcessorDepsFromStore } from '@src/services/clipProcessorDepsFromStore';
import { scopeRedubToClip } from '@src/services/dubbing/reviewRedubScope';
import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import {
  deriveReelTranscriptStructure,
  stampReelTranscriptWords,
} from '@src/services/reel/reelTranscriptStructure';
import { EMPTY_REEL } from '@src/services/reel/reelTypes';
import SaveStoryAndVideoService, {
  createVideoCreatorSaveAdapter,
} from '@src/services/SaveStoryAndVideoService';
import SavePullQuoteService from '@src/services/transcript/SavePullQuoteService';
import {
  StoreContext,
  useDatoClient,
  useStore,
  useTranscription,
  useTranslationStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import {
  deleteElementMetaKeys,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import {
  getLastStoryLanguage,
  recordLastStoryLanguage,
} from '@src/utility/lastStoryLanguage';
import { LogManager } from '@src/utility/Logger';
import { removeOverlayAndPersist } from '@src/utility/overlayDeletion';
import { addOverlayFromTranscript } from '@src/utility/overlayInsertion';
import {
  applyPlacementEdgeOverrides,
  readElementTimelineRange,
} from '@src/utility/placementOverrides';
import { dispatchTranscriptSeek } from '@src/utility/transcriptEvents';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { deriveClipTimeRange } from '@src/utility/videoTranscriptionUtils';
import {
  computeMarkerQuickEditFields,
  resolveQuickEditTargetId,
} from '@src/videoTranscriptionProcessor/translation/computeMarkerQuickEditFields';
import { extractTranslatableElements } from '@src/videoTranscriptionProcessor/translation/extractTranslatableElements';
import { getTranslatableSourceElements } from '@src/videoTranscriptionProcessor/translation/getTranslatableSourceElements';
import { readActiveCaptionLanguage } from '@src/videoTranscriptionProcessor/translation/readActiveCaptionLanguage';
import { routeRetranslateTargetId } from '@src/videoTranscriptionProcessor/translation/routeRetranslateTargetId';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { parseTranslationViewMode } from '../transcript/alignedBlocks';
import { useTimelineMarkers } from '../transcript/hooks/useTimelineMarkers';
import {
  CLIP_REVIEW_MODE_PARAM,
  CLIP_REVIEW_PARAM,
  isClipReviewRequested,
  stripClipPreviewParams,
} from './clipPreviewUrlState';
import { ClipVersionPreviewGuard } from './ClipVersionPreviewChrome';
import { createClipRootProxy } from './hooks/clipTranscriptProxies';
import { useClipDubComposition } from './hooks/useClipDubComposition';
import { useCompositionPinDispatchers } from './hooks/useCompositionPinDispatchers';
import { useFrozenWhileInactive } from './hooks/useFrozenWhileInactive';
import { instrumentReelTranscriptCollapse } from './instrumentReelTranscriptCollapse';
import { ReelNameGenerationDialog } from './ReelEditor/ReelNameGenerationDialog';
import { useReelSectionNaming } from './ReelEditor/useReelSectionNaming';
import { useSectionSpeakers } from './ReelEditor/useSectionSpeakers';

import type { CompositionTemplate } from '../sidepanel/CompositionSettings/types';
import type { TimelineMarker } from '../transcript/hooks/useTimelineMarkers';
import type { ClipVideoCreatorAdapter } from './hooks/ClipVideoCreatorAdapter';
import type { TranslationViewMode } from '@src/components/transcript/alignedBlocks';
import type { RetranslateCompositionDispatch } from '@src/components/transcript/CompositionQuickEditPopover';
import type { ReDubSelectionRequest } from '@src/components/transcript/hooks/useSpanReplace';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { HighlightHoverInfo } from '@src/hooks/useHighlightHoverPopover';
import type { RedubLanded } from '@src/services/dubbing/redubLanded';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type {
  ClipEditSessionStore,
  VideoClipPreview,
} from '@src/stores-v2/ClipEditSessionStore';
import type { Story } from '@src/types.ts/story';
import type { OverlayTranscriptRange } from '@src/utility/overlayInsertion';

const logger = LogManager.getLogger('component:ClipTranscriptBridge');

interface ClipTranscriptBridgeProps {
  editorStore: ClipPreviewEditorStore | null;
  adapter: ClipVideoCreatorAdapter | null;
  /**
   * Selected transcript display language (`null` = Original). Owned by the
   * modal's ClipEditSessionStore (PLAT-10199) so the "Edit in Creator Studio"
   * button can carry it across navigation.
   */
  selectedLanguage: string | null;
  onSelectedLanguageChange: (lang: string | null) => void;
  /**
   * PLAT-11150 — false while the Clip Preview is showing another tab. The pane
   * stays mounted (its scroll position, selection and effects are hard-won),
   * but its transcript is served frozen so a reel edit no longer re-renders a
   * word list nobody is looking at. Defaults to active when omitted.
   */
  isVisible?: boolean;
  /**
   * PLAT-11877 — the host's Languages tab pane. With it, the language list
   * renders there (through a portal) and the toolbar globe opens that tab;
   * without it (reviewer mode), the list opens over the transcript.
   */
  languagesPane?: HTMLElement | null;
  /** Switch to the Languages tab. */
  onOpenLanguages?: () => void;
  /** Switch back to the Transcript tab. */
  onCloseLanguages?: () => void;
  /**
   * Clip-renderer inputs forwarded to the Translation Review modal so it can
   * mount its OWN renderer for the edited montage (PLAT-10286 Step 3). Present
   * on the clip-preview entry; the modal gates on them.
   */
  story?: Story;
  sessionStore?: ClipEditSessionStore;
  clip?: VideoClipPreview;
  /**
   * PLAT-11163 — the transcript's way back to the Reel Editor. The modal owns
   * the tabs, so it owns what "open the Reel Editor at this Section" means.
   */
  onOpenSectionInReelEditor?: (sectionId: string) => void;
}

interface ClipTranscriptBridgeLoadedProps {
  editorStore: ClipPreviewEditorStore;
  adapter: ClipVideoCreatorAdapter;
  selectedLanguage: string | null;
  onSelectedLanguageChange: (lang: string | null) => void;
  /**
   * PLAT-11150 — false while the Clip Preview is showing another tab. The pane
   * stays mounted (its scroll position, selection and effects are hard-won),
   * but its transcript is served frozen so a reel edit no longer re-renders a
   * word list nobody is looking at. Defaults to active when omitted.
   */
  isVisible?: boolean;
  /**
   * PLAT-11877 — the host's Languages tab pane. With it, the language list
   * renders there (through a portal) and the toolbar globe opens that tab;
   * without it (reviewer mode), the list opens over the transcript.
   */
  languagesPane?: HTMLElement | null;
  /** Switch to the Languages tab. */
  onOpenLanguages?: () => void;
  /** Switch back to the Transcript tab. */
  onCloseLanguages?: () => void;
  story?: Story;
  sessionStore?: ClipEditSessionStore;
  clip?: VideoClipPreview;
  /**
   * PLAT-11163 — the transcript's way back to the Reel Editor. The modal owns
   * the tabs, so it owns what "open the Reel Editor at this Section" means.
   */
  onOpenSectionInReelEditor?: (sectionId: string) => void;
}

/**
 * Inner component — props are always non-null so hooks need no `!` assertions.
 */
const ClipTranscriptBridgeLoaded: React.FC<ClipTranscriptBridgeLoadedProps> =
  observer(
    ({
      editorStore,
      adapter,
      selectedLanguage,
      onSelectedLanguageChange,
      isVisible,
      languagesPane,
      onOpenLanguages,
      onCloseLanguages,
      story,
      sessionStore,
      clip,
      onOpenSectionInReelEditor,
    }) => {
      const realRootStore = useStore();
      const videoCreator = useVideoCreatorStore();
      const datoClientStore = useDatoClient();
      const userIdentity = useUserIdentity();
      // PLAT-10948 — this surface owns `?clipTranslationReview` (open state +
      // view-mode granularity) so a shared/refreshed clip link restores the
      // review. Clip-preview scope only; the modal writes the other clip params.
      const [searchParams, setSearchParams] = useSearchParams();
      const {
        contentStudioTranscriptAddPhoto,
        enableTranscriptOverlayAdd,
        enableTranscriptOverlayRangeAdjust,
        enableTranslationReview,
        enableCompositionCanvasQuickEdit,
        enableTranslationReviewerMode,
        enableReelTranscriptStructure,
        contentStudioEnableTranslatedUndoRedo,
        dubbingMaxMinutes,
        stemSeparationMaxMinutes,
      } = useFlagsCombination(userIdentity.currentRole);

      // Hydration must run against the real (story-level) videoCreator, BEFORE
      // the clip-scoped proxy providers wrap the subtree below. Translation
      // data is story-level (singleton store, shared across studios); only the
      // per-view selection is local.
      const supportedLanguages = useTranslationStoreHydration();
      const translationStore = useTranslationStore();
      const transcriptionStore = useTranscription();
      const generateModal = useTranslationGenerateModalController();

      // Whether this story's music has been separated from its speech, and
      // where the kept music lives. The SAME controller the Transcript &
      // Speakers tab mounts, not a second reading of the same thing.
      //
      // Called here, above the language dropdown, on purpose. Separation is a
      // story-wide operation of some minutes; lifting the controller above the
      // dropdown is what lets a person close the menu, or switch the modal to
      // another tab, without disturbing it. The outcome is raised as a toast by
      // this surface rather than by the control for the same reason — the
      // control may well be gone by the time the answer arrives.
      const musicSeparation = useMusicSeparationController(
        videoCreator.storyId ?? '',
        {
          onDone: () => {
            videoCreator.showToast({
              state: 'success',
              message: 'Original music separated',
            });
          },
          onError: () => {
            videoCreator.showToast({
              state: 'error',
              message: "Couldn't separate voice and music. Try again.",
            });
          },
        },
      );

      // The Voice control's state, its dubbed-language list, and the handler
      // that puts the dub into THIS clip's own composition. Called here, above
      // the clip store providers below, so it reads the story-level stores.
      //
      // The handler takes no undo copy and asks for no save: the adapter's
      // replacement of `executeCommand` already does both.
      const dubComposition = useClipDubComposition({
        adapter,
        editorStore,
        selectedLanguage,
        // The separation state feeds the same plan the dub is built from: the
        // bed's address, and the gate that keeps a press from landing before
        // that address is known.
        musicController: musicSeparation,
      });

      /**
       * PLAT-10628 — the controller the clip's music line runs on: the story's
       * separation controller with its two music fields replaced by the clip's
       * own (its POSITION read off the clip's elements, its PRESS running this
       * clip's composition as one undoable command). Everything else is
       * story-wide and comes through unchanged. On this branch the player's
       * Music picker reads `dubComposition` directly; this remains for the
       * dub popover's "Keep Original Music" box below.
       */
      const clipMusicController = useMemo(
        () => ({
          ...musicSeparation,
          musicMode: dubComposition.musicMode,
          setMusicMode: dubComposition.onMusicModeChange ?? (() => undefined),
        }),
        [
          dubComposition.musicMode,
          dubComposition.onMusicModeChange,
          musicSeparation,
        ],
      );

      // PLAT-11877 — where the player bar wants the Voice / Music pickers
      // (the modal's header row); `null` on a surface with no slot.
      const playerSettingsSlot = usePlayerSettingsSlot();

      // The dub popover's "Keep Original Music" box. On the clip controller,
      // so ticking it puts the music on THIS clip's composition once the bed
      // exists; separation itself is story-wide and capped on the recording.
      const keepOriginalMusic = useKeepOriginalMusic({
        controller: clipMusicController,
        durationSec: videoCreator.story?.originalVideo?.video?.duration,
        maxMinutes: stemSeparationMaxMinutes,
      });

      // How much of this clip has no dubbed voice. Keyed on the language the
      // CONTROL is showing, not the transcript's: opening from the clip grid
      // leaves the transcript on Original while the clip plays its dub, and
      // keying on the transcript would measure the wrong dub. The token is
      // bumped when a dub run finishes, so the warning re-reads the manifest it
      // just changed instead of describing the gap it filled.
      const [dubCoverageRefresh, setDubCoverageRefresh] = useState(0);
      // PLAT-12024 — the lines a re-dub just re-recorded, for the pulse and
      // the toast's "Show Passage".
      const [redubLanded, setRedubLanded] = useState<RedubLanded | null>(null);
      const landRedub = useCallback((segmentIds: readonly string[]) => {
        if (segmentIds.length === 0) return;
        setRedubLanded({ segmentIds, at: new Date().toISOString() });
      }, []);
      const revealRedub = useCallback(() => {
        setRedubLanded(prev =>
          prev ? { ...prev, revealToken: (prev.revealToken ?? 0) + 1 } : prev,
        );
      }, []);
      // PLAT-11877 — the Languages panel over the transcript pane.
      const [languagesOpen, setLanguagesOpen] = useState(false);
      const isClipOriginalVideoElement = useCallback(
        (probe: unknown) => adapter.isOriginalVideoElement(probe),
        [adapter],
      );
      // PLAT-11440 — measured for the language being READ as well as the one
      // being HEARD. They are the same language most of the time, and the hook
      // drops the repeat, so this reads twice only on the clip that needs it: a
      // Russian dub playing while the transcript is switched to Spanish. Without
      // the second read the Spanish button has no lines to send.
      // Every dubbed language is measured too: the language menu's Regenerate
      // Dub can be pressed on any dubbed row, and it re-dubs this clip's lines.
      const dubCoverageByLanguage = useDubCoverage({
        editorStore,
        isOriginalVideoElement: isClipOriginalVideoElement,
        languages: [
          dubComposition.controlLanguage,
          selectedLanguage,
          ...dubComposition.dubbedLanguages,
        ],
        refreshToken: dubCoverageRefresh,
      });
      const dubCoverage = dubComposition.controlLanguage
        ? (dubCoverageByLanguage[dubComposition.controlLanguage] ?? null)
        : null;
      // The same sum for the language on screen, which is the one a new dub is
      // offered in. The coverage notice keeps the control's language: it
      // describes the dub being heard, not the one being offered.
      const selectedLanguageCoverage = selectedLanguage
        ? (dubCoverageByLanguage[selectedLanguage] ?? null)
        : null;

      // Dub just the lines THIS clip is missing. The run is additive — what is
      // already voiced is reused — so pressing this never re-records a line the
      // creator has approved. Hosted here because this is the only surface
      // that knows which lines the clip is missing.
      const fillGapsModal = useDubGenerateModalController();
      // PLAT-12022 — the selection re-dub popover, for a selection made in
      // the clip-hosted Translation Review. Anchored beside the passage.
      const redubSelection = useRedubSelectionController();

      // PLAT-11663 — "Remove Dub" from a dubbed row's menu, the story's dub
      // for that language (every clip, not only this one — the confirm says
      // so). Afterwards the dubbed-language list is re-read (nothing here
      // observes the server) and, if this clip was playing that dub, the
      // audio falls back to Original.
      const captionForLanguage = useCallback(
        (code: string) =>
          supportedLanguages.find(l => l.value === code)?.caption ??
          formatLanguageCaption(code),
        [supportedLanguages],
      );
      const dubCompositionRef = useRef(dubComposition);
      dubCompositionRef.current = dubComposition;
      const removeDub = useRemoveDub({
        storyId: videoCreator.storyId,
        repository: datoClientStore.dubbingRepository,
        captionForLanguage,
        showToast: useCallback(
          (toast: { state: 'success' | 'error'; message: string }) =>
            videoCreator.showToast(toast),
          [videoCreator],
        ),
        onRemoved: useCallback((language: string) => {
          const composition = dubCompositionRef.current;
          composition.refreshDubbedLanguages?.();
          // Re-measure: the coverage still counts the removed dub's lines as
          // voiced, which leaves "Dub with AI Voice" with no lines to offer.
          setDubCoverageRefresh(token => token + 1);
          if (composition.appliedDubLanguage === language) {
            composition.onAudioModeChange?.('original');
          }
        }, []),
      });

      // The stretch of the recording this clip keeps. Source: the clip's stored
      // startTime/duration; fallback: derive from transcriptPosition over the
      // story elements. Shared by the translation review (its playback bound)
      // and the dubbing cap below. (PLAT-10286, hard problem B)
      //
      // Deps are the read values, NOT `adapter`/`transcriptionStore`: both
      // store identities are stable for the mounted preview (the adapter is a
      // ref — hence `currentVideo: false` in its makeObservable), so keying on
      // them would derive once at mount, before the clip JSON and transcript
      // have loaded, and never again on an aspect switch.
      const clipJson = adapter.currentVideo?.clipJson;
      const storyElements = transcriptionStore.transcriptionElements;
      const clipTimeRange = useMemo(
        () => deriveClipTimeRange(clipJson, storyElements),
        [clipJson, storyElements],
      );

      // PLAT-11532 — a dub here covers this clip only, so the cap is measured
      // against the clip's own length, not the story's. Set ⇒ the Dub button
      // and the coverage notice's fill action are disabled and say why. A clip
      // whose range cannot be derived is not gated: a working dub beats a
      // guess.
      const clipDubUnavailableNote = getDubDurationLimitNote(
        clipTimeRange ? clipTimeRange.end - clipTimeRange.start : undefined,
        dubbingMaxMinutes,
        'clip',
      );

      const gapPhraseIds = dubCoverage?.gaps.map(gap => gap.phraseId) ?? [];
      const handleFillGaps = useCallback(() => {
        const language = dubComposition.controlLanguage;
        if (!language || gapPhraseIds.length === 0 || clipDubUnavailableNote)
          return;
        fillGapsModal.open(language, {
          segmentIds: gapPhraseIds,
          fillGaps: true,
        });
      }, [
        clipDubUnavailableNote,
        dubComposition.controlLanguage,
        fillGapsModal,
        gapPhraseIds,
      ]);

      /**
       * Re-record the clip's lines whose words were edited after they were
       * dubbed.
       *
       * The opposite run to the gap fill, and the popover's third scoped mode:
       * `segmentIds` with neither `fillGaps` nor `firstDub` FORCES re-synthesis
       * of lines that already have audio (see `isSpanReDub` in
       * `DubGenerateModal`), which is the only way an out-of-date line is
       * fixed — the additive fill above skips it precisely because it is
       * already voiced. Each speaker's current voice is reused, so the clip
       * comes back in the same voices saying the current words.
       *
       * Same cap as the fill: a clip too long to dub is too long to re-dub.
       */
      const stalePhraseIds =
        dubCoverage?.stale.map(line => line.phraseId) ?? [];
      const handleReDubStale = useCallback(() => {
        const language = dubComposition.controlLanguage;
        if (!language || stalePhraseIds.length === 0 || clipDubUnavailableNote)
          return;
        fillGapsModal.open(language, { segmentIds: stalePhraseIds });
      }, [
        clipDubUnavailableNote,
        dubComposition.controlLanguage,
        fillGapsModal,
        stalePhraseIds,
      ]);

      /**
       * PLAT-11440 — dub THIS CLIP in a language the story has no dub in at all.
       *
       * The same popover and the same clip-sized run the gap fill uses, pointed
       * at the language being READ. With no dub anywhere, every one of this
       * clip's lines is a gap, so the gap list already IS the list of lines this
       * clip covers and there is nothing else to build.
       *
       * A press before the sum has come back names no lines, and a run naming no
       * lines would dub nothing, so it does not start.
       */
      const selectedGapPhraseIds =
        selectedLanguageCoverage?.gaps.map(gap => gap.phraseId) ?? [];
      const handleDubThisClip = useCallback(() => {
        if (
          !selectedLanguage ||
          selectedGapPhraseIds.length === 0 ||
          clipDubUnavailableNote
        )
          return;
        fillGapsModal.open(selectedLanguage, {
          segmentIds: selectedGapPhraseIds,
          firstDub: true,
        });
      }, [
        clipDubUnavailableNote,
        fillGapsModal,
        selectedGapPhraseIds,
        selectedLanguage,
      ]);

      /**
       * Regenerate Dub from the language menu: re-dub THIS CLIP's lines in that
       * language, never the whole story's. A whole-story regenerate started
       * from a 30-second clip re-recorded every line of the story and ran past
       * the backend's time limit.
       *
       * Every line the clip shows, dubbed or not, including the ones crossing
       * its start or end — the same lines "Dub this clip" names. The rest of the
       * story's dub is reused. As with `handleDubThisClip`, a press before that
       * language's sum has come back names no lines, so it does not start.
       */
      const handleRegenerateClipDub = useCallback(
        (language: string) => {
          const lineIds = dubCoverageByLanguage[language]?.lineIds ?? [];
          if (lineIds.length === 0 || clipDubUnavailableNote) return;
          fillGapsModal.open(language, { segmentIds: lineIds });
        },
        [clipDubUnavailableNote, dubCoverageByLanguage, fillGapsModal],
      );

      /**
       * PLAT-11382 — a re-dub asked for from inside Translation Review, over
       * the lines the reviewer selected.
       *
       * It opens the same popover as the two runs above, in the same forced
       * mode `handleReDubStale` uses (`segmentIds` with neither `fillGaps` nor
       * `firstDub`), so lines that already have audio get new audio. The
       * direction field is part of that popover.
       *
       * `scopeRedubToClip` narrows it to this clip: a selection that names
       * lines this clip has no dubbed voice for is not a re-take at all, and a
       * clip that is too long to dub is too long to re-dub. Lines whose words
       * were EDITED after they were dubbed are exactly what this run is for and
       * are kept.
       *
       * That is all it does. Comparing this clip's words against the story's —
       * and refusing a whole-story dub when they differ — is PLAT-11379, a
       * later phase 4 ticket that is deliberately not specified yet.
       */
      const clipUndubbedLineIds = gapPhraseIds;
      const handleReDubReviewSelection = useCallback(
        (language: string, request: ReDubSelectionRequest) => {
          const decision = scopeRedubToClip({
            scope: 'clip',
            selectedLineIds: request.segmentIds,
            undubbedLineIds: clipUndubbedLineIds,
            unavailableReason: clipDubUnavailableNote?.tooltip,
          });
          if (decision.lineIds.length === 0) {
            if (decision.refusal) {
              videoCreator.showToast({
                state: 'error',
                message: decision.refusal,
              });
            }
            return;
          }
          redubSelection.open(language, {
            ...request,
            segmentIds: decision.lineIds,
          });
        },
        [
          clipDubUnavailableNote,
          clipUndubbedLineIds,
          redubSelection,
          videoCreator,
        ],
      );

      // PLAT-9626 — the clip preview runs commands against its own clip-scoped
      // editor, which has the phrase resolver wired at construction (see
      // `ClipPreviewEditorStore` creation in `useClipPreviewRenderer` /
      // `TranscriptSpeakersAdapter`). No per-component registration needed.

      /**
       * PLAT-11150 — whether this pane is the one on screen.
       *
       * A MobX box rather than a prop read directly, because the proxy behind
       * it is built once in a `useMemo` and has to see the current value on
       * every read. It is also the mechanism that thaws the surface: while
       * frozen this box is the only observable the transcript is subscribed to,
       * so setting it back to `true` is what invalidates those observers.
       */
      const paneActive = isVisible !== false;
      const paneActiveBox = useMemo(() => observable.box(paneActive), []);
      useEffect(() => {
        runInAction(() => paneActiveBox.set(paneActive));
      }, [paneActiveBox, paneActive]);

      const clipRootProxy = useMemo(() => {
        const clipVideoSourceStore = adapter.getVideoSourceStore();
        return createClipRootProxy(
          realRootStore,
          editorStore,
          clipVideoSourceStore,
          paneActiveBox,
        );
      }, [realRootStore, editorStore, adapter, paneActiveBox]);

      /**
       * PLAT-11150 — this component's own transcript read, through the proxy.
       *
       * The bridge is an `observer` and stays mounted while its tab is hidden,
       * so reading `editorStore.transcriptElements` here would subscribe it to
       * every transcript replacement and re-render it on every reel edit — the
       * exact subscription the freeze exists to detach, reinstated one level
       * above the surfaces it protects. The proxy read gives the same array
       * while the pane is active, and the frozen one while it is not. See
       * `createFrozenElementsReader`.
       */
      const clipTranscriptElements =
        clipRootProxy.transcriptionStore.transcriptionElements;

      // PLAT-9626 — language change must re-produce the clip's karaoke track
      // so the rendered video captions follow the dropdown. Delegates to the
      // shared helper used by Creator Studio's CaptionsLanguageHeaderToggle;
      // scoped to the clip's own editor (no global mutation) and skips the
      // window-event + showSubtitles propagation Creator Studio needs.
      // PLAT-10635: the org's first custom caption template for this clip's
      // aspect ratio, used to seed captions on translate instead of the
      // built-in "Impact" default. Shared by the Captions picker, the language
      // switch and the Translation Review player (all produce captions from
      // it). PLAT-11877 — ONE rule for every surface
      // (`getDefaultCaptionTemplateConfig`): the head of the template list
      // below, at the CLIP's aspect ratio, so what the picker names as the
      // default is exactly what a first "on" applies.
      const currentAspectRatio = videoCreator.getCurrentAspectRatio();
      const karaokeTextBranding =
        videoCreator.organization?.karaokeTextBranding;
      // PLAT-11702 — the review shows a captionless clip in the showcase's
      // MOST RECENTLY used caption template (else its first), at the CLIP's
      // aspect ratio. Review display only; the CAP path above keeps its seed.
      const clipAspectRatio =
        adapter?.getCurrentAspectRatio() ?? currentAspectRatio;
      const lastUsedTemplates = videoCreator.organization?.lastUsedTemplates;
      // PLAT-11702 follow-up — the whole list, so the review's Captions
      // dropdown can offer every one of the showcase's styles for a clip with
      // none of its own. The head IS the preferred template, so the seed below
      // cannot drift from what the picker shows as selected.
      const reviewCaptionTemplates = useMemo(
        () =>
          videoCreator.textBrandingService.getCustomKaraokeTemplateConfigs(
            clipAspectRatio,
          ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [videoCreator, clipAspectRatio, karaokeTextBranding, lastUsedTemplates],
      );
      const captionFallbackTemplate = reviewCaptionTemplates[0];
      const reviewCaptionFallbackTemplate = captionFallbackTemplate;
      // PLAT-11702 follow-up — names an id the list above does not carry (a
      // preset, a language variant, another aspect ratio), so the review can
      // name the row standing for the video's own caption style.
      const resolveCaptionTemplateName = useCallback(
        (templateId: string) =>
          videoCreator.textBrandingService.getKaraokeTemplateTitle(templateId),
        [videoCreator],
      );
      const isPresetCaptionTemplate = useCallback(
        (templateId: string) =>
          videoCreator.textBrandingService.isPresetTemplate(templateId),
        [videoCreator],
      );
      // The Captions picker beside the player draws each template's name in
      // its own face, as the review does; the brand faces have to be loaded
      // for that (the review workspace loads them for itself).
      useLoadBrandKitFonts(videoCreator);

      const onLanguageChange = useCallback(
        (next: string | null) => {
          onSelectedLanguageChange(next);
          if (!editorStore.editor) return;
          // PLAT-10788 (DS6) — load the clip's cached per-language variant when
          // one exists; produce only on a miss. Targets the clip's OWN store.
          const result = switchCaptionLanguage({
            editor: editorStore.editor,
            karaokeStore: editorStore.karaokeStore,
            lang: next,
            transcriptLanguage:
              videoCreator.rootStore?.transcriptionStore?.rawLanguage,
            originalVideoLanguage: videoCreator.story?.originalVideoLanguage,
            fallbackTemplate: captionFallbackTemplate,
            // PLAT-10816 — the user's own pick. Also what persists it: the clip
            // adapter gates its autosave on the same signal.
            createUndoRedoEntry: true,
            // PLAT-11416 — the org's curated per-language variant of the
            // active template family, at the CLIP's aspect ratio (the story
            // AR can differ from the clip's selected dimension).
            resolveLanguageVariant: (parentTemplateId, languageKey) =>
              videoCreator.textBrandingService.getKaraokeLanguageVariantConfig(
                parentTemplateId,
                languageKey,
                adapter?.getCurrentAspectRatio() ??
                  videoCreator.getCurrentAspectRatio(),
              ),
          });
          switch (result.kind) {
            case 'noop':
            case 'no-config':
            case 'no-track':
              return;
            case 'applied':
              editorStore.refreshKaraokeState();
              // PLAT-11416 — re-filter the clip's template picker for the
              // new language.
              adapter?.textBrandingService?.refreshKaraokeTemplatesForLanguage();
              // PLAT-9966: re-fire the clip's debounced setSource so the
              // composition translation projection picks up the new active
              // language. Uses the same VideoSourceStore the clip's renderer
              // is connected to via the adapter.
              adapter?.getVideoSourceStore().refreshSource();
              // PLAT-11409 (DE1) — passive notice when the caption font was
              // auto-swapped/restored for the language's script.
              notifyCaptionFontSubstitution(
                toast => videoCreator.showToast(toast),
                result,
              );
              return;
            case 'produce-failed':
              logger.error('commandProduceKaraoke returned false', {
                currentLang: result.currentLang,
                nextLang: result.nextLang,
              });
              return;
            case 'exception':
              logger.error('Failed to re-produce karaoke for language change', {
                currentLang: result.currentLang,
                nextLang: result.nextLang,
                ...extractErrorInfo(result.error),
              });
              return;
          }
        },
        [
          editorStore,
          adapter,
          onSelectedLanguageChange,
          captionFallbackTemplate,
        ],
      );
      // PLAT-11898 — a user's own pick is what the story remembers; the
      // fallbacks and the captions-view sync below switch without recording.
      const onLanguagePick = useCallback(
        (next: string | null) => {
          recordLastStoryLanguage(story?.id, next);
          onLanguageChange(next);
        },
        [story?.id, onLanguageChange],
      );

      // PLAT-10657 — a clip's persisted `KaraokeConfig.language` is whatever it
      // was last published in, so push the modal's opening selection into the
      // clip's editor or foreign captions render over English transcript text.
      const reconciledLanguageRef = useRef(false);
      useEffect(() => {
        if (reconciledLanguageRef.current) return;
        const editor = editorStore.editor;
        // Wait until karaoke is fully loaded — only then does the default config
        // carry the persisted (possibly foreign) language.
        if (!editor || editorStore.karaokeLines.length === 0) return;
        // PLAT-10948 — validate a URL-seeded language here, not via the
        // fallback effect below (whose ordering depends on which data loads
        // first): on a cache miss `switchCaptionLanguage` PRODUCES a variant.
        if (selectedLanguage && supportedLanguages.length === 0) return;
        const validLanguage =
          selectedLanguage &&
          supportedLanguages.some(l => l.value === selectedLanguage)
            ? selectedLanguage
            : null;
        reconciledLanguageRef.current = true;
        const result = switchCaptionLanguage({
          editor,
          karaokeStore: editorStore.karaokeStore,
          lang: validLanguage,
          transcriptLanguage:
            videoCreator.rootStore?.transcriptionStore?.rawLanguage,
          originalVideoLanguage: videoCreator.story?.originalVideoLanguage,
          // PLAT-11416 — a reconcile that produces fresh captions honors the
          // org's curated per-language variant, same as a user switch (at
          // the clip's aspect ratio).
          resolveLanguageVariant: (parentTemplateId, languageKey) =>
            videoCreator.textBrandingService.getKaraokeLanguageVariantConfig(
              parentTemplateId,
              languageKey,
              adapter?.getCurrentAspectRatio() ??
                videoCreator.getCurrentAspectRatio(),
            ),
        });
        if (result.kind === 'applied') {
          editorStore.refreshKaraokeState();
          adapter?.getVideoSourceStore().refreshSource();
          // PLAT-11409 (DE1) — a reconcile that had to produce fresh captions
          // may have font-substituted; surface the same notice as a user switch.
          notifyCaptionFontSubstitution(
            toast => videoCreator.showToast(toast),
            result,
          );
        }
        // selectedLanguage is intentionally read once at reconcile time (the
        // modal's opening selection); later dropdown changes flow through
        // onLanguageChange.
      }, [editorStore, adapter, editorStore.karaokeLines, supportedLanguages]);

      // PLAT-10199 — a translation edit updates the store but doesn't re-produce
      // the clip's karaoke track, so the rendered captions keep the stale
      // machine translation until the language is re-toggled. Re-produce +
      // refresh on edit, scoped to the clip's own editor.
      const refreshClipKaraoke = useCallback(() => {
        editorStore.refreshKaraokeState();
        adapter?.getVideoSourceStore().refreshSource();
      }, [editorStore, adapter]);
      // PLAT-10945 — the refresh above is display-only. Without a save the
      // rebuilt track never reaches storage, so a reload shows the clip's
      // pre-correction captions while the prose view shows the correction.
      // `refreshClipKaraoke` runs first and copies the rebuilt lines into the
      // clip's own KaraokeStore (`refreshKaraokeState` → `syncFromEditor`),
      // which is exactly what `prepareCurrentVideoForSave` harvests. This is
      // the adapter's existing save entry point — background, re-entrancy
      // guarded, autoSave-flavoured — not a new one.
      const persistClipKaraoke = useCallback(() => {
        void adapter?.saveCurrentAndParentVideos(true, true, true);
      }, [adapter]);
      useReproduceCaptionsOnTranslationEdit(
        editorStore.editor,
        translationStore,
        refreshClipKaraoke,
        persistClipKaraoke,
      );

      // PLAT-9700: fall back to Original when the active translation is
      // removed. Reuses the same `onLanguageChange` path so the karaoke
      // re-produce branch fires too — keeps the clip preview's rendered
      // captions in lockstep with the dropdown.
      useLanguageRemovalFallback(selectedLanguage, () =>
        onLanguageChange(null),
      );

      // PLAT-10948 — `?clipLang=` is seeded raw, so fall back to Original
      // for a code this org doesn't support. The removal fallback above only sees
      // live removals, never a code that was never there.
      useEffect(() => {
        if (!selectedLanguage) return;
        if (supportedLanguages.length === 0) return; // org not loaded yet
        if (supportedLanguages.some(l => l.value === selectedLanguage)) return;
        logger.warn('Unsupported clipLang — falling back to Original', {
          selectedLanguage,
          supported: supportedLanguages.map(l => l.value),
        });
        onLanguageChange(null);
      }, [selectedLanguage, supportedLanguages, onLanguageChange]);

      // PLAT-11898 — the opening language (a link's `clipLang`, the clip's or
      // the story's memory) is seeded without waiting on the story's
      // translations. Confirm it in the background; if the story no longer has
      // it (deleted elsewhere), fall back to Original and forget it, unless the
      // user has picked something else since.
      const [openingLanguage] = useState(selectedLanguage);
      useMissingTranslationCheck(story?.id, openingLanguage, () => {
        if (selectedLanguage !== openingLanguage) return;
        onLanguageChange(null);
        if (getLastStoryLanguage(story?.id) === openingLanguage) {
          recordLastStoryLanguage(story?.id, null);
        }
      });

      // ── Services (mirroring LeftSidePanel) ──────────────────────────
      const saveService = useMemo(
        () =>
          new SaveStoryAndVideoService({
            videoCreator: createVideoCreatorSaveAdapter(videoCreator),
          }),
        [videoCreator],
      );

      const savePullQuoteService = useMemo(
        () => new SavePullQuoteService({ datoClientStore }),
        [datoClientStore],
      );

      const clipSaveService = useMemo(() => {
        const service = new SaveClipFromPreviewService({
          source: adapter,
          // The adapter stands in for the store, its rootStore proxied to the
          // clip's own transcript, editor and captions.
          clipProcessorDeps: clipProcessorDepsFromStore(
            adapter as unknown as IVideoCreatorStore,
          ),
        });
        return {
          saveClip: async (input: {
            startIndex: number;
            endIndex: number;
            relevantElements?: any[];
          }) => {
            const result = await service.save({
              startIndex: input.startIndex,
              endIndex: input.endIndex,
              relevantElements: input.relevantElements,
            });
            if (!result.ok) throw new Error(result.error.message);
            return result.value;
          },
        };
      }, [adapter]);

      // PLAT-11752 — the Save as Clip toast's "Open Clip" action.
      //
      // Deliberately NOT an in-place swap of `videoClipPreview`: the preview's
      // renderer is initialised once per mount (`useClipPreviewRenderer` bails
      // with "Renderer already initialized", and its effect watches only
      // `!!videoSource`), so replacing the clip prop updates the title and the
      // transcript while the player goes on showing the previous clip's video.
      // Dropping the preview first unmounts the modal and tears the renderer
      // down; `resolveClipPreviewSync` then reopens it from `?clipId` on the
      // next render, which is the same path every other entry point takes.
      // The other clip-preview params describe the clip being replaced, so they
      // go with it.
      const handleOpenSavedClip = useCallback(
        (clipId: string) => {
          const saved = videoCreator.story?.otherVideos?.find(
            v => v.id === clipId,
          );
          if (!saved) {
            logger.warn('Saved clip not found for toast action', { clipId });
            return;
          }
          logger.info('Reopening the preview on the saved clip', {
            clipId,
            fromClipId: videoCreator.videoClipPreview?.id,
          });
          videoCreator.videoClipPreview = null;
          setSearchParams(
            prev => {
              const params = stripClipPreviewParams(new URLSearchParams(prev));
              params.set('clipId', clipId);
              return params;
            },
            { replace: true },
          );
        },
        [videoCreator, setSearchParams],
      );

      const { addMediaToTimeline } = useAddMediaToTimeline({
        editorStore,
        videoCreator,
      });

      const mediaService = useMemo(
        () => ({
          addPhotoToTimeline: async (input: {
            item: {
              id: string;
              src: string;
              description: string;
              aspectRatio: number;
              alt: string;
              type: 'artifact' | 'stock' | 'ai';
            };
            time: number;
            duration: number;
            track: number;
            useComposition: boolean;
            id: string;
          }) => {
            await addMediaToTimeline(input.item, 'photo', {
              time: input.time,
              duration: input.duration,
              track: input.track,
              useComposition: input.useComposition,
              id: input.id,
            });
          },
        }),
        [addMediaToTimeline],
      );

      const clipVideoSourceStore = useMemo(
        () => adapter.getVideoSourceStore(),
        [adapter],
      );

      // PLAT-10915 — AI text generation for this clip: the automatic first run
      // when a Smart Graphics card is inserted, and the explicit run behind the
      // control's Generate / Retry. Built once here so both share the
      // per-control in-flight / error state — a failure at insert is still
      // reported, with its retry, when quick edit is opened later (DS3).
      const aiTextGenerationContext = useMemo(
        () => ({
          storyId: videoCreator.storyId ?? undefined,
          showcaseId: adapter.organization?.id,
          clipId: adapter.currentVideo?.id,
        }),
        [
          videoCreator.storyId,
          adapter.organization?.id,
          adapter.currentVideo?.id,
        ],
      );

      const getCurrentVideoForAIText = useCallback(
        () => adapter.currentVideo,
        [adapter],
      );
      const refreshSourceForAIText = useCallback(
        () => clipVideoSourceStore.refreshSource(),
        [clipVideoSourceStore],
      );

      const aiTextGeneration = useAITextGeneration({
        surface: 'content-studio-clip-preview',
        editorStore,
        getCurrentVideo: getCurrentVideoForAIText,
        refreshSource: refreshSourceForAIText,
        context: aiTextGenerationContext,
        transcriptElements: clipTranscriptElements,
      });

      // PLAT-10105 — "Add Overlay Graphic" from a transcript selection. The
      // shared Transcript resolves the selection to a timeline range and hands
      // it here; this surface opens the filtered overlay picker (rendered under
      // the REAL stores, outside the clip proxy, so it lists the org's overlay
      // templates) and inserts the chosen overlay bound to that range.
      const [overlaySelection, setOverlaySelection] =
        useState<OverlayTranscriptRange | null>(null);

      const handleAddOverlayGraphicRequested = useCallback(
        (range: OverlayTranscriptRange) => {
          setOverlaySelection(range);
        },
        [],
      );

      const handleSelectOverlay = useCallback(
        async (template: CompositionTemplate) => {
          const range = overlaySelection;
          if (!range) return;
          setOverlaySelection(null);

          // Shared add-overlay sequence (analytics + insert bound to the
          // highlighted range + repaint + error toast). Same helper the
          // Creator Studio surface (`LeftSidePanel`) uses; only the `surface`
          // tag and the video-like object differ. See `addOverlayFromTranscript`.
          await addOverlayFromTranscript({
            template,
            range,
            surface: 'content-studio-clip-preview',
            editorStore,
            video: {
              storyId: videoCreator.storyId,
              duration: adapter.duration,
              getCurrentVideo: () => adapter.currentVideo,
              getCurrentAspectRatio: () => adapter.getCurrentAspectRatio(),
              organization: adapter.organization,
              story: adapter.story,
              convertToVh: (v: string) => adapter.textProducer.convertToVh(v),
              setActiveElements: (id: string) => adapter.setActiveElements(id),
              setTime: (time: number) => adapter.setTime(time),
            },
            // Repaint the iframe + let the PLAT-10045 underline pick up the
            // new overlay's range.
            refreshSource: () => clipVideoSourceStore.refreshSource(),
            // User-facing error — uses the shared story-level toast surface the
            // transcript flows already use.
            onError: message => {
              videoCreator.showToast({
                state: 'error',
                message,
              });
            },
            // PLAT-10915 — the graphic arrives written: every `aiText` control
            // on the inserted card generates from its configured scope, without
            // the insert waiting on (or being able to fail because of) it.
            onAITextControlsCreated: aiTextGeneration.generateForNewComposition,
            logger,
          });
        },
        [
          overlaySelection,
          editorStore,
          adapter,
          videoCreator,
          clipVideoSourceStore,
          aiTextGeneration,
        ],
      );

      /**
       * PLAT-11150 — markers are frozen with the transcript.
       *
       * `useTimelineMarkers` reads `editorStore.transcriptElements` directly
       * rather than through the proxy, and rebuilds over the whole array on
       * every timeline change. Freezing only the transcript would therefore
       * still re-render the word list while hidden, because
       * `TranscriptionText`'s memo compares `timelineMarkers` too.
       *
       * Passing `null` while hidden skips both the subscription and the
       * rebuild; `useFrozenWhileInactive` then holds the last computed markers
       * so the hidden pane keeps showing what it last showed rather than
       * blanking its chips.
       */
      const liveTimelineMarkers = useTimelineMarkers(
        paneActive ? adapter : null,
        paneActive ? editorStore : null,
      );
      const timelineMarkers = useFrozenWhileInactive(
        liveTimelineMarkers,
        paneActive,
      );

      // PLAT-9968 — composition quick-edit popover state. Tracks which chip
      // is open (if any). Cleared on close, on outside click (handled inside
      // the popover via `useOutsideAlerter`), or when the marker is removed.
      const [openMarker, setOpenMarker] = useState<{
        marker: TimelineMarker;
        anchor: HTMLElement;
      } | null>(null);

      // PLAT-10046 — read the active caption language so the popover knows
      // whether to render translated rows. `selectedLanguage` is the bridge's
      // existing dep that the captions picker mutates via
      // `applyCaptionsLanguage`, so the memo invalidates on every language
      // flip. The popover treats this value as static-at-mount; flipping
      // language while the popover is open unmounts it (parent unmount on
      // language change).
      const activeLangForPopover = useMemo(
        () => readActiveCaptionLanguage(editorStore.editor),
        [editorStore, selectedLanguage],
      );

      // PLAT-10046 — per-language composition-phrase lookup. The popover
      // consumes this to resolve translated values per row, decide which
      // footer action to surface, and compute the shared-phrase confirm
      // copy. The Map identity comes from `getCompositionPhrasesByNormalized
      // Source`, which internally memoizes per `TranslationData` reference —
      // so we MUST include the live `translations.get(lang)` reference in
      // the dep list. Without it, the bridge re-renders after a cache swap
      // but `useMemo` returns the stale Map (deps unchanged), and the
      // popover re-opens showing pre-save values.
      const activeLangTranslationData =
        translationStore.translations.get(activeLangForPopover) ?? null;
      const compositionPhrases = useMemo(
        () =>
          translationStore.getCompositionPhrasesByNormalizedSource(
            activeLangForPopover,
          ),
        [translationStore, activeLangForPopover, activeLangTranslationData],
      );

      // PLAT-10066 — human-readable name for the active caption language,
      // appended to the popover header ("Edit Graphic Overlay (Spanish)")
      // so the user is never confused about which language pin they're
      // editing under translated mode. `null` under original-language —
      // the bridge omits the suffix so the header reads exactly as
      // PLAT-9968 shipped it.
      const activeLanguageDisplay = useMemo<string | null>(() => {
        if (isOriginalLang(activeLangForPopover)) return null;
        return formatLanguageCaption(activeLangForPopover);
      }, [activeLangForPopover]);

      // PLAT-10046 — wrap the store's retranslate method into a
      // surface-agnostic closure. The popover passes back only
      // `{ force, compositionElementId?, sourceTexts? }`; this closure
      // injects the live `compositionTargets` (extracted against the
      // clip-local source) + `lang` so the popover doesn't need access to
      // the source store or the active-language read.
      const retranslateCompositionDispatch: RetranslateCompositionDispatch =
        useCallback(
          async ({ force, compositionElementId, sourceTexts }) => {
            // Canonical (non-windowed) clip elements from the clip editor, not
            // the windowed preview source — see `getTranslatableSourceElements`.
            const extraElementData =
              (adapter.currentVideo?.extraElementData as
                | Record<string, unknown>
                | undefined) ?? null;
            const { compositionTargets } = extractTranslatableElements({
              videoSourceElements: getTranslatableSourceElements(
                editorStore.editor,
              ),
              extraElementData,
              targetLanguage: activeLangForPopover,
            });
            const result = await translationStore.retranslateCompositionPhrases(
              {
                lang: activeLangForPopover,
                compositionTargets,
                force,
                // PLAT-10875: the active workspace's showcase, so the backend
                // honors this workspace's TM even if the story is in several.
                showcaseId: videoCreator.organization?.id,
                ...routeRetranslateTargetId(
                  compositionElementId,
                  compositionTargets,
                ),
                ...(sourceTexts ? { sourceTexts } : {}),
              },
            );
            // PLAT-10046 — explicit refresh after the cache swap, mirroring
            // the upsert path. The PLAT-9966 reaction on
            // `translations.get(lang)` should already fire, but this
            // guarantees the iframe paints the new translated text the
            // moment the API call resolves rather than relying on the
            // reaction-scheduler + setSource debounce alignment.
            clipVideoSourceStore.refreshSource();
            return result;
          },
          [
            adapter,
            editorStore,
            clipVideoSourceStore,
            activeLangForPopover,
            translationStore,
            videoCreator,
          ],
        );

      // PLAT-10046 — manual pin upsert + revert dispatchers. Shared with
      // the canvas surface via `useCompositionPinDispatchers`; see that
      // hook for the references[] inheritance + `refreshSource()` rationale.
      const refreshClipSource = useCallback(() => {
        clipVideoSourceStore.refreshSource();
      }, [clipVideoSourceStore]);
      const {
        upsert: upsertCompositionPhraseDispatch,
        revert: removeCompositionPhrasePinDispatch,
      } = useCompositionPinDispatchers({
        compositionElementId: openMarker?.marker.id ?? null,
        compositionPhrases,
        lang: activeLangForPopover,
        translationStore,
        editorDisplayName: userIdentity.currentEditor,
        onAfterCommit: refreshClipSource,
      });

      // Quick-edit fields for an arbitrary marker — shared by the open popover
      // (`popoverFields`) and the transcript hover pill so both gate the Edit
      // affordance on the same "has editable fields" rule (PLAT-10120; mirrors
      // the canvas action menu). Logic lives in the shared
      // `computeMarkerQuickEditFields` util.
      const computeMarkerFields = useCallback(
        (marker: TimelineMarker) => {
          const topLevel = (
            clipVideoSourceStore.source as {
              elements?: ReadonlyArray<Record<string, unknown>>;
            }
          )?.elements;
          const extraElementData =
            (adapter.currentVideo?.extraElementData as
              | Record<string, unknown>
              | undefined) ?? null;
          return computeMarkerQuickEditFields(
            topLevel,
            extraElementData,
            marker,
          );
        },
        [clipVideoSourceStore.source, adapter],
      );

      const popoverFields = useMemo(
        () => (openMarker ? computeMarkerFields(openMarker.marker) : []),
        [openMarker, computeMarkerFields],
      );

      const handleMarkerClick = useCallback(
        (marker: TimelineMarker, anchor: HTMLElement) => {
          // Single-popover policy — clicking a different chip swaps the
          // anchor; clicking the same chip closes (parity with most
          // chip/popover UIs in the plugin).
          let opened = false;
          setOpenMarker(prev => {
            if (prev && prev.marker.id === marker.id) return null;
            opened = true;
            return { marker, anchor };
          });
          // Seek the clip-preview player to the midpoint of the
          // composition's time range so the user sees the rendered overlay
          // frame they're about to edit. Without this the player stays
          // parked at 00:00 and the canvas shows a frame that may not
          // contain the overlay.
          if (opened) {
            const midpoint = (marker.time + marker.endTime) / 2;
            if (Number.isFinite(midpoint)) {
              adapter.renderer?.setTime(midpoint).catch(error => {
                logger.warn('Failed to seek to composition midpoint', {
                  targetId: marker.id,
                  midpoint,
                  ...extractErrorInfo(error),
                });
              });
            }
          }
        },
        [adapter],
      );

      const closePopover = useCallback(() => setOpenMarker(null), []);

      // Fire the analytics open event when the popover becomes visible with
      // fields to show. Shared with the canvas surface via the hook so the
      // two paths can't drift on signature shape or `hasTranslation`
      // derivation.
      useCompositionQuickEditOpenedTracking({
        isOpen: !!openMarker,
        targetId: openMarker?.marker.id ?? null,
        compositionType: openMarker?.marker.type ?? null,
        fields: popoverFields,
        storyId: videoCreator.storyId ?? null,
        surface: 'content-studio-clip-preview',
      });

      const handleSaved = useCallback(
        (summary: {
          originalRowsCommitted: number;
          translatedRowsCommitted: number;
        }) => {
          if (!openMarker) return;
          if (summary.originalRowsCommitted === 0) return;
          // Only fire `composition_text_edited` here — the popover owns
          // `composition_phrase_edited` for translated-value pins so it
          // doesn't have to expose per-row dirty state to the caller.
          // PLAT-10066 — `language` records the active caption language at
          // save time so dashboards can distinguish "original-text edited
          // while viewing original" from "original-text edited while
          // viewing Spanish" (= a non-translatable / quick-edit row).
          const storyId = videoCreator.storyId ?? undefined;
          const language = isOriginalLang(activeLangForPopover)
            ? '<original>'
            : activeLangForPopover;
          for (const f of popoverFields) {
            trackCompositionTextEdited({
              story_id: storyId,
              composition_element_id: openMarker.marker.id,
              composition_type: openMarker.marker.type,
              surface: 'content-studio-clip-preview',
              language,
              source_text_length: f.originalValue.length,
            });
          }
        },
        [openMarker, popoverFields, videoCreator, activeLangForPopover],
      );

      // PLAT-10045 — hover-popover for overlay-underlined transcript ranges
      // shows the SAME pencil/trash/X pill the canvas surface uses
      // (`CompositionCanvasActionMenu`) so the two entry surfaces look and
      // behave identically. Anchored above the hovered transcript word; the
      // pill's `transform: translate(-50%, -50%)` wrapper self-centers on
      // the anchor regardless of pill width.
      const handleOpenOverlay = useCallback(
        (overlayId: string, anchor: HTMLElement) => {
          const marker = timelineMarkers.overlays.find(m => m.id === overlayId);
          if (!marker) return;
          handleMarkerClick(marker, anchor);
        },
        [timelineMarkers.overlays, handleMarkerClick],
      );

      const handleDeleteOverlay = useCallback(
        (overlayId: string) => {
          if (!editorStore) return;
          try {
            // Shared delete-an-overlay sequence (command path + repaint + meta
            // cleanup + post-delete flush) — see `removeOverlayAndPersist`
            // (PLAT-10191). Flush rides the clip adapter's background autosave.
            const removed = removeOverlayAndPersist(overlayId, {
              editor: editorStore.editor,
              video: adapter.currentVideo ?? null,
              refreshSource: () => clipVideoSourceStore.refreshSource(),
              persist: () =>
                adapter.saveStoryAndVideo(false, true, false, true),
              logger,
            });

            logger.info(
              'Timeline overlay removed via transcript hover action menu',
              { targetId: overlayId, removed },
            );
          } catch (error) {
            logger.error('Timeline overlay removal failed', {
              targetId: overlayId,
              ...extractErrorInfo(error),
            });
          }
        },
        [editorStore, clipVideoSourceStore, adapter],
      );

      // Commit a re-anchored overlay range. `time`/`duration` are resolved by
      // the transcript from the boundary words. Route through the command path
      // (markDirty + undo) then refresh the source for an immediate repaint —
      // same belt-and-suspenders as the delete path above.
      const handleAdjustOverlayRange = useCallback(
        (
          overlayId: string,
          {
            time,
            duration,
            anchorStartInitialIndex,
            anchorEndInitialIndex,
          }: {
            time: number;
            duration: number;
            anchorStartInitialIndex?: number;
            anchorEndInitialIndex?: number;
          },
        ) => {
          if (!editorStore) return;
          // Keep the overlay's transcript anchor in sync with the dragged
          // range, else the anchor-derived underline (findOverlayRanges)
          // snaps back to the old words on the next render.
          //
          // PLAT-10921 — BEFORE the command, not after. `commandUpdateElement`
          // runs a placement reconcile on its way out; with the anchor still
          // pointing at the old words, that reconcile re-derives from them and
          // drags a pinned `selection` overlay straight back.
          if (
            anchorStartInitialIndex != null &&
            anchorEndInitialIndex != null
          ) {
            upsertElementMetaObject(adapter.currentVideo, overlayId, {
              transcriptAnchor: {
                startInitialIndex: anchorStartInitialIndex,
                endInitialIndex: anchorEndInitialIndex,
              },
            });
          } else {
            // The dragged range crosses a reordered section boundary, so it
            // cannot be word-bound (`clampAnchorToSelectionRun`). Drop the
            // anchor rather than leave the old one — that is precisely the
            // snap-back the comment above warns about. With no anchor the
            // graphic binds to the time window it was just given, which is the
            // correct binding once playback order diverges from source order.
            deleteElementMetaKeys(adapter.currentVideo, overlayId, [
              'transcriptAnchor',
            ]);
          }
          // PLAT-10921 — record the timeline edges the creator changed. No
          // transcript passed: the anchor rewrite above already IS the
          // `selection` Rebind, from the very words this range was resolved
          // from. Mirrors the Creator Studio surface (`LeftSidePanel`).
          const previous = readElementTimelineRange(
            editorStore.editor,
            overlayId,
          );
          if (previous) {
            applyPlacementEdgeOverrides({
              editor: editorStore.editor,
              video: adapter.currentVideo,
              elementId: overlayId,
              previous,
              next: { time, duration },
            });
          }

          const ok = editorStore.editor.commandUpdateElement(overlayId, {
            time,
            duration,
          });
          if (ok) clipVideoSourceStore.refreshSource();
        },
        [editorStore, clipVideoSourceStore, adapter],
      );

      const handleCommitOverlayTiming = useCallback(
        (next: { time: number; duration: number }) => {
          if (!editorStore || !openMarker) return;
          const elementId = openMarker.marker.id;
          // PLAT-10921 — likewise BEFORE the command. The popover commits raw
          // times with no words involved, so a `selection` graphic's Rebind has
          // to derive its new anchor from the transcript here. The thunk means
          // only a `selection` graphic ever pays for building it. Mirrors
          // `LeftSidePanel`.
          const previous = readElementTimelineRange(
            editorStore.editor,
            elementId,
          );
          if (previous) {
            applyPlacementEdgeOverrides({
              editor: editorStore.editor,
              video: adapter.currentVideo,
              elementId,
              previous,
              next,
              transcript: () => editorStore.editor.getFullTranscript(),
            });
          }
          const ok = editorStore.editor.commandUpdateElement(elementId, next);
          if (ok) clipVideoSourceStore.refreshSource();
        },
        [editorStore, clipVideoSourceStore, openMarker, adapter],
      );

      const renderHighlightPopover = useCallback(
        (info: HighlightHoverInfo) => {
          if (!info.overlayId) return null;
          const overlayId = info.overlayId;
          const marker = timelineMarkers.overlays.find(m => m.id === overlayId);
          // If the overlay id has no corresponding marker the hover-pill
          // would have nowhere to dispatch its click, so skip rendering.
          if (!marker) return null;
          // Pill label stays language-agnostic ("Edit Graphic Overlay") —
          // the language suffix lives on the popover header (PLAT-10066),
          // not on the hover affordance, so the pill doesn't widen under
          // longer language names.
          const headerLabel = formatHeaderLabelFromMarker(marker);
          const editLabel = `Edit ${headerLabel}`;
          // Gate Edit on the same rule as the canvas action menu: an overlay
          // with no editable fields has nothing to open, so disable Edit
          // instead of letting a click silently no-op (PLAT-10120 follow-up).
          const editDisabled = computeMarkerFields(marker).length === 0;
          // Portal to document.body so the pill escapes the modal's
          // stacking + transform context. Inside the modal, an ancestor
          // transform breaks `position: fixed` (it becomes positioned
          // relative to the transformed ancestor, not the viewport),
          // which both mispositions the pill and stacks it below the
          // iframe canvas. document.body has no such ancestor.
          return createPortal(
            <TranscriptHoverMenuPosition
              data-testid="transcript-highlight-action-popover"
              style={{
                // Anchor coords describe the pill's CENTER. The styled
                // wrapper's translate(-50%, -50%) handles self-centering on
                // the anchor point.
                left: `${info.anchorRect.left + info.anchorRect.width / 2}px`,
                top: `${info.anchorRect.top - 24}px`,
              }}
              onMouseEnter={info.onMouseEnter}
              onMouseLeave={info.onMouseLeave}
            >
              <CompositionCanvasActionMenu
                ariaLabel={editLabel}
                editTooltip={editLabel}
                editDisabled={editDisabled}
                editDisabledReason="This overlay has no editable text"
                onEdit={anchor =>
                  handleOpenOverlay(overlayId, anchor ?? info.anchorElement)
                }
                onAdjustRange={info.onAdjustRange}
                onDelete={() => handleDeleteOverlay(overlayId)}
              />
            </TranscriptHoverMenuPosition>,
            document.body,
          );
        },
        [
          timelineMarkers.overlays,
          handleOpenOverlay,
          handleDeleteOverlay,
          computeMarkerFields,
        ],
      );

      // PLAT-10948 — one shareable param: PRESENCE reopens the review,
      // VALUE restores its granularity (an unparseable value, incl. the older
      // `=1`, still opens on the default view). Orphaned without `?clipLang`
      // = no-op.
      const [reviewOpen, setReviewOpen] = useState(() =>
        isClipReviewRequested(searchParams),
      );
      // The granularity is held here, not just read from the URL, because
      // reopening has to write the mode the user last chose.
      const [reviewViewMode, setReviewViewMode] = useState<TranslationViewMode>(
        () =>
          parseTranslationViewMode(searchParams.get(CLIP_REVIEW_PARAM)) ??
          'segment',
      );
      // PLAT-11662 — which job the review opens on: from the control that
      // opened it, or from the link; null lets the workspace apply its default.
      const [reviewMode, setReviewMode] = useState<ReviewMode | null>(() => {
        const raw = searchParams.get(CLIP_REVIEW_MODE_PARAM);
        return raw === 'translation' || raw === 'dubbing' ? raw : null;
      });
      // Written here, not via the modal's consolidated writer: a review toggle
      // never coincides with a clip/tab/lang/playback change, so this separate
      // `replace` can't lose that writer's stale-closure race (see the modal).
      const writeReviewParams = useCallback(
        (value: TranslationViewMode | null, mode: ReviewMode | null) => {
          setSearchParams(
            prev => {
              const params = new URLSearchParams(prev);
              if (value) params.set(CLIP_REVIEW_PARAM, value);
              else params.delete(CLIP_REVIEW_PARAM);
              if (value && mode) params.set(CLIP_REVIEW_MODE_PARAM, mode);
              else params.delete(CLIP_REVIEW_MODE_PARAM);
              return params;
            },
            { replace: true },
          );
        },
        [setSearchParams],
      );
      const setReviewOpenAndUrl = useCallback(
        (open: boolean, mode: ReviewMode | null = reviewMode) => {
          setReviewOpen(open);
          setReviewMode(mode);
          writeReviewParams(open ? reviewViewMode : null, mode);
        },
        [writeReviewParams, reviewViewMode, reviewMode],
      );
      // PLAT-12047 — a re-dub asked for on this clip's own transcript is
      // handed to the clip's review (see the tab host for the why); the
      // review's hand-off routes through `handleReDubReviewSelection`, so
      // the clip scoping still runs.
      const [pendingHandoff, setPendingHandoff] =
        useState<ReviewHandoff | null>(null);
      const reviewHandoffNonceRef = useRef(0);
      const handOffToReview = useCallback(
        (request: ReviewHandoffRequest) => {
          reviewHandoffNonceRef.current += 1;
          setPendingHandoff(
            reviewHandoffFrom(request, reviewHandoffNonceRef.current),
          );
          setReviewOpenAndUrl(
            true,
            request.kind === 'fix' ? 'translation' : 'dubbing',
          );
        },
        [setReviewOpenAndUrl],
      );
      useEffect(() => {
        if (!reviewOpen) setPendingHandoff(null);
      }, [reviewOpen]);
      // PLAT-12056 — Fix Translation always hands off where the review is
      // enabled; a re-dub only when the language on screen has a dub and the
      // clip is within the dubbing cap, as on the Transcript tab.
      const clipOverDubCap = !!clipDubUnavailableNote;
      const selectedLanguageDubbed =
        !!selectedLanguage &&
        dubComposition.dubbedLanguages.includes(selectedLanguage);
      const reviewHandoff = useMemo<ReviewHandoffProp | undefined>(
        () =>
          enableTranslationReview && selectedLanguage
            ? {
                request: handOffToReview,
                redub:
                  !!dubComposition.onAudioModeChange &&
                  selectedLanguageDubbed &&
                  !clipOverDubCap,
              }
            : undefined,
        [
          enableTranslationReview,
          selectedLanguage,
          handOffToReview,
          dubComposition.onAudioModeChange,
          selectedLanguageDubbed,
          clipOverDubCap,
        ],
      );
      const setReviewViewModeAndUrl = useCallback(
        (next: TranslationViewMode) => {
          setReviewViewMode(next);
          writeReviewParams(next, reviewMode);
        },
        [writeReviewParams, reviewMode],
      );
      const setReviewModeAndUrl = useCallback(
        (next: ReviewMode) => {
          setReviewMode(next);
          writeReviewParams(reviewViewMode, next);
        },
        [writeReviewParams, reviewViewMode],
      );

      // Story-absolute playback window of the previewed clip — bounds the
      // review workspace's compact player (it plays the ORIGINAL story video;
      // there is no separate clip asset for lightweight clips). Primary
      // source: the clip's stored startTime/duration; fallback: derive from
      // transcriptPosition over the story elements. Underivable → open the
      // review UNBOUNDED with a warning (a working review without the bound
      // beats a broken entry). (PLAT-10286, hard problem B)
      const reviewTimeRange = useMemo(() => {
        if (!reviewOpen) return undefined;
        if (!clipTimeRange) {
          logger.warn(
            'translation-review: clip time range underivable — opening unbounded',
            { storyId: videoCreator.storyId },
          );
        }
        return clipTimeRange;
      }, [reviewOpen, clipTimeRange, videoCreator]);

      // Loading-placeholder poster for the review player = the clip's own
      // rendered thumbnail (the same still the clip preview shows), so the
      // compact player isn't black while the whole-story video loads + seeks
      // to the clip's start. Cleared by the player once the real frame decodes
      // (PLAT-10286). Falls back to the story cover in the player if absent.
      const reviewPoster = useMemo(
        () => (reviewOpen ? adapter.currentVideo?.thumbnail?.url : undefined),
        [reviewOpen, adapter],
      );

      // The workspace's open-time 'transcript-pause' broadcast only reaches
      // TranscriptVideoPlayer listeners; the clip preview's own player is the
      // renderer IFRAME, which does not listen. Pause it explicitly so
      // opening review mid-playback never leaves audio running behind the
      // workspace (PLAT-10286, prerequisite 4).
      useEffect(() => {
        if (!reviewOpen) return;
        void adapter.renderer?.pause();
        runInAction(() => {
          adapter.isPlaying = false;
        });
      }, [reviewOpen, adapter]);

      // PLAT-11157 — publish the reel's structure to the transcript's prose.
      // A context rather than a prop: the shared transcript component is under
      // a prop-count ratchet, and the other three hosts must stay literally
      // untouched — they have no provider, so they render today's transcript.
      // Gated entirely here: flag off, not a reel, or nothing derived all
      // publish `null`. The stamp is handed down as a function because the
      // prose renders a projection whose indices drift from
      // `getFullTranscript()`'s — the renderer stamps the array it renders.
      //
      // Every dep is a primitive or a write-signal, deliberately:
      // `clipTranscriptElements` (NOT `editorStore.transcriptElements`) is the
      // PLAT-11150 frozen read, so a reel edit while this tab is hidden does
      // not re-render the transcript; and `adapter.currentVideo` is never a
      // dep — its identity changes on every variant write (thumbnail, render
      // poll, autosave), none of which changes the structure.
      const currentReel = adapter.currentVideo?.clipJson?.reel;
      const currentIsReel = !!adapter.currentVideo?.clipJson?.isReel;
      // Resolves the Sections' `spk_X` labels to people, so two labels for the
      // same person count once in the reel's summary line. Only fetched for
      // a reel whose structure is on — the hook clears itself on an absent
      // story id — and both underlying fetches are cached per story.
      const reelSpeakersByLabel = useSectionSpeakers(
        enableReelTranscriptStructure && currentIsReel
          ? (videoCreator.storyId ?? undefined)
          : undefined,
        datoClientStore,
      );
      // PLAT-11158 — session collapse state. Lives here (not the modal, not a
      // store): the bridge stays mounted across tab switches and unmounts when
      // the clip preview closes, which IS the ticket's lifetime.
      const reelCollapse = useReelTranscriptCollapse();
      // PLAT-11160 — a Section heading seeks like a word click does: the
      // scoped seek event (other transcripts ignore it) plus the adapter's
      // own setTime.
      const openSectionInReelEditor = useCallback(
        (section: { sectionId: string }) => {
          trackReelTranscriptEditorRevealed({
            surface: 'clip_preview',
            section_id: section.sectionId,
          });
          onOpenSectionInReelEditor?.(section.sectionId);
        },
        [onOpenSectionInReelEditor],
      );
      // PLAT-11257 — the naming a Section carries, offered where the Section is
      // read as well as where it is arranged. The same hook the Reel Editor
      // uses: one product rule about replacing a typed name (DE10), one write,
      // two surfaces.
      const [nameDialogTarget, setNameDialogTarget] = useState<{
        id: string;
        currentName?: string;
      } | null>(null);
      const getNamingTimeline = useCallback(
        () => editorStore.editor?.timeline,
        [editorStore],
      );
      // Derived at call time rather than held: this pane renders a frozen
      // transcript while the Reel Editor tab is showing (PLAT-11150), so a list
      // captured here could be older than the reel the creator is naming. Only
      // live Sections reach the transcript's structure, so anything found is on.
      const getNameableSection = useCallback(
        (sectionId: string) => {
          const timeline = editorStore.editor?.timeline;
          if (!timeline) return undefined;
          const section = deriveReelSections(timeline).find(
            candidate => candidate.id === sectionId,
          );
          return section
            ? { elementIds: section.elementIds, enabled: true }
            : undefined;
        },
        [editorStore],
      );
      const trackSectionRenamed = useCallback(
        (target: { nameSourceBefore: 'user' | 'generated' | 'clip' }) =>
          trackReelSectionRenamed({
            clip_id: clip?.id,
            name_source_before: target.nameSourceBefore,
          }),
        [clip?.id],
      );
      const {
        renameSection,
        generateSectionName,
        generatingIds: generatingNameSectionIds,
        errors: sectionNameErrors,
      } = useReelSectionNaming({
        adapter,
        getTimeline: getNamingTimeline,
        getSection: getNameableSection,
        context: {
          storyId: story?.id,
          showcaseId: story?.primaryShowcase?.id,
          clipId: clip?.id,
        },
        datoClientStore,
        onConfirmOverUserName: setNameDialogTarget,
        onRenamed: trackSectionRenamed,
      });
      const renameReelSection = useCallback(
        (section: { sectionId: string }, name: string) =>
          renameSection(section.sectionId, name),
        [renameSection],
      );
      const generateReelSectionName = useCallback(
        (
          section: { sectionId: string; storedName?: string },
          mode: 'auto' | 'instruct',
        ) => {
          if (mode === 'instruct') {
            // Straight to the dialog: the creator asked to say what the name
            // should say, so there is nothing to check first.
            setNameDialogTarget({
              id: section.sectionId,
              currentName: section.storedName,
            });
            return;
          }
          // `userNameConfirmed`, deliberately: picking this item on this
          // Section's own menu IS the ask DE10 requires, and a dialog on top of
          // it would make the plain item the slower of the two ways to run the
          // same prompt. The steered run is the item beside it.
          void generateSectionName(section.sectionId, {
            userNameConfirmed: true,
          });
        },
        [generateSectionName],
      );
      const seekToReelSection = useCallback(
        (section: { startTime: number; sectionId: string; index: number }) => {
          trackReelTranscriptSectionSeeked({
            surface: 'clip_preview',
            section_id: section.sectionId,
            section_index: section.index,
          });
          dispatchTranscriptSeek(section.startTime, adapter);
          void adapter.setTime(section.startTime);
        },
        [adapter],
      );
      // The derived half, memoized WITHOUT the collapse state. Folding a
      // Chapter is a way of reading, and it used to change this object's
      // identity — which re-derived the whole structure here AND re-stamped
      // every token in both transcript views, on every fold click. The two
      // memos are split so that only a real timeline or reel change pays for
      // a derivation, and `stampWords` keeps one identity across folds for the
      // views' own stamp memos to key on.
      const reelStructureBase = useMemo(() => {
        if (!enableReelTranscriptStructure || !currentIsReel) return null;
        if (!editorStore.isReady || !editorStore.editor) return null;
        const structure = deriveReelTranscriptStructure(
          editorStore.editor.timeline,
          currentReel ?? EMPTY_REEL,
        );
        if (structure.sections.length === 0) return null;
        return {
          structure,
          // Reads the timeline at call time rather than capturing it — the
          // editor reassigns `timeline` on a source reload, and a captured
          // one would stamp against detached elements.
          stampWords: (
            elements: Parameters<ReelTranscriptStructureView['stampWords']>[0],
          ) => stampReelTranscriptWords(editorStore.editor.timeline, elements),
        };
      }, [
        enableReelTranscriptStructure,
        currentIsReel,
        currentReel,
        editorStore,
        editorStore.isReady,
        // The change signal for timeline edits, through the freeze.
        clipTranscriptElements,
      ]);

      const reelStructureView =
        useMemo<ReelTranscriptStructureView | null>(() => {
          if (!reelStructureBase) return null;
          return {
            ...reelStructureBase,
            speakersByLabel: reelSpeakersByLabel,
            collapse: instrumentReelTranscriptCollapse(
              reelCollapse,
              reelStructureBase.structure,
              'clip_preview',
            ),
            onSectionSeek: seekToReelSection,
            onSectionOpenInEditor: onOpenSectionInReelEditor
              ? openSectionInReelEditor
              : undefined,
            onSectionRename: renameReelSection,
            // No Dato client, no prompt to resolve — the heading offers the
            // rename alone rather than an item that cannot run.
            onSectionGenerateName: datoClientStore
              ? generateReelSectionName
              : undefined,
            generatingNameSectionIds,
            sectionNameErrors,
          };
        }, [
          reelStructureBase,
          reelSpeakersByLabel,
          reelCollapse,
          seekToReelSection,
          onOpenSectionInReelEditor,
          openSectionInReelEditor,
          renameReelSection,
          generateReelSectionName,
          generatingNameSectionIds,
          sectionNameErrors,
          datoClientStore,
        ]);

      // PLAT-11164 — the structure first rendering for a clip, once per clip:
      // the memo re-derives on every edit, so the guard is the clip's id, not
      // the derivation.
      const structureShownForClipRef = useRef<string | null>(null);
      const structureClipId = clip?.id ?? null;
      useEffect(() => {
        if (!reelStructureView || structureClipId === null) return;
        if (structureShownForClipRef.current === structureClipId) return;
        structureShownForClipRef.current = structureClipId;
        trackReelTranscriptStructureShown({
          surface: 'clip_preview',
          story_id: videoCreator.storyId ?? undefined,
          clip_id: structureClipId,
          section_count: reelStructureView.structure.sections.length,
          chapter_count: reelStructureView.structure.chapters.length,
        });
      }, [reelStructureView, structureClipId, videoCreator.storyId]);

      // The transcript is showing caption lines (Transcript's own test).
      const inCaptionsView =
        !!adapter.showSubtitles && (editorStore.karaokeLines?.length ?? 0) > 0;

      // PLAT-11877 — the language control, built once and placed three ways:
      // the toolbar's globe, the title line of a translation, and the
      // Languages panel over the pane (see `placeLanguageControls`).
      const languagePlaces = placeLanguageControls(
        <TranslationToggle
          selectedLanguage={selectedLanguage}
          onLanguageChange={onLanguagePick}
          onRequestGenerate={generateModal.onRequestGenerate}
          // PLAT-11563: a row action names its language; the review
          // workspace follows the displayed one, so switch first.
          // Review Translation on a language that already has a dub opens on
          // Dubbing; the header tabs still switch back.
          onRequestReview={(language, mode) => {
            onLanguagePick(language);
            setReviewOpenAndUrl(
              true,
              dubComposition.dubbedLanguages.includes(language)
                ? 'dubbing'
                : mode,
            );
          }}
          generateDisabled={generateModal.isOpen}
          // The Voice control needs more than the callback to paint:
          // `dubbedLanguages` is what makes it appear for a language
          // the story has a dub for, and `appliedDubLanguage` is what
          // keeps it on screen for a clip already playing a dub
          // whatever language the transcript is showing. Dropping
          // either hides the control however correctly the callback
          // is wired. `onRequestDub` is deliberately NOT passed —
          // generating a dub stays on the Transcript & Speakers tab;
          // this surface only applies an existing one. It also drives
          // the "Re-dub" entry in the kebab beside the Voice toggle,
          // which redoes the whole STORY's dub. PLAT-11440 wires the
          // narrower `onRequestFirstDub` instead: the button alone,
          // for a language the story has no dub in, dubbing this clip
          // only.
          onRequestFirstDub={
            dubComposition.onAudioModeChange ? handleDubThisClip : undefined
          }
          // PLAT-11663 — the dub group of a DUBBED row's menu, as on
          // the story tab: Regenerate Dub and Remove Dub. Here Regenerate
          // re-dubs this clip's lines only, not the story's whole dub.
          onRequestRegenerateDub={
            dubComposition.onAudioModeChange
              ? handleRegenerateClipDub
              : undefined
          }
          onRequestRemoveDub={
            dubComposition.onAudioModeChange ? removeDub.request : undefined
          }
          onRemoveDubForLanguage={
            dubComposition.onAudioModeChange ? removeDub.remove : undefined
          }
          dubUnavailableNote={clipDubUnavailableNote}
          audioMode={dubComposition.audioMode}
          appliedDubLanguage={dubComposition.appliedDubLanguage}
          onAudioModeChange={dubComposition.onAudioModeChange}
          // While a run is out on the network the control paints as
          // working and cannot be pressed. Without this a person sees
          // nothing happen, presses again, and both runs place a full
          // set of dubbed audio on the clip.
          audioModeBusy={dubComposition.audioModeBusy}
          dubbedLanguages={dubComposition.dubbedLanguages}
          // PLAT-11877 — Voice and Music sit beside the player (the
          // header row's `PlayerAudioPickers`), not in this menu.
          audioControlsInPlayer
          // PLAT-11899 — the clip's Dub Level, saved on release like the
          // Voice toggle. Not on Original: no dubbed voice is heard there.
          // PLAT-11927 — on Both, the original's level above it.
          dubLevelControl={
            dubComposition.onVoiceVolumeChange &&
            dubComposition.audioMode !== 'original' ? (
              <>
                {dubComposition.audioMode === 'both' &&
                  dubComposition.onOriginalVolumeChange && (
                    <DubLevelRow
                      label="Original Level"
                      level={dubComposition.originalVolume}
                      onChange={dubComposition.onOriginalVolumeChange}
                    />
                  )}
                <DubLevelRow
                  level={dubComposition.voiceVolume}
                  onChange={dubComposition.onVoiceVolumeChange}
                />
              </>
            ) : undefined
          }
          // Renders nothing at all when the clip is fully dubbed:
          // showing the warning only when there IS a gap keeps it
          // from becoming wallpaper people stop reading.
          coverageNotice={({ close }) => (
            <DubCoverageNotice
              coverage={dubCoverage}
              tooltipId={LANGUAGE_MENU_HELP_TOOLTIP_ID}
              // PLAT-11585 — close the menu first so it does not sit
              // on top of the progress modal this opens.
              onFillGaps={() => {
                close();
                handleFillGaps();
              }}
              // Same close-first reason: the re-dub opens the same
              // progress modal the fill does.
              onReDubStale={() => {
                close();
                handleReDubStale();
              }}
              busy={fillGapsModal.isOpen}
              fillGapsDisabledNote={clipDubUnavailableNote?.tooltip}
              // With the Voice control on Original the published file
              // keeps the original recording, so a figure about the
              // dub describes a file nobody is going to get.
              audioMode={dubComposition.audioMode}
              // Reached when the story HAS a dub in this language but
              // none of this clip's own lines are in it: the clip
              // publishes with the original voice, which is correct,
              // and must not be reported as seconds of silence.
              noDubLanguageLabel={
                dubComposition.controlLanguage
                  ? (supportedLanguages.find(
                      language =>
                        language.value === dubComposition.controlLanguage,
                    )?.caption ??
                    formatLanguageCaption(dubComposition.controlLanguage))
                  : undefined
              }
            />
          )}
          showLanguageHeader
          supportedLanguages={supportedLanguages}
          surface="content-studio-clip-preview"
        />,
        languagesOpen,
        setLanguagesOpen,
        languagesPane && onOpenLanguages && onCloseLanguages
          ? {
              target: languagesPane,
              open: onOpenLanguages,
              close: onCloseLanguages,
            }
          : undefined,
      );

      // PLAT-11877 — the player's settings beside the player: Captions on a
      // captionable video, Voice / Music for the language being read once it
      // has a dub. The composition is this clip's, so the pickers are this
      // clip's too: the bar shows them only while this bridge is on screen.
      // Rendered inside the clip-scoped provider below, because Captions
      // reads the clip's own karaoke store.
      const dubAudio =
        dubComposition.onAudioModeChange &&
        !!selectedLanguage &&
        dubComposition.dubbedLanguages.includes(selectedLanguage)
          ? {
              audioMode: dubComposition.audioMode,
              onAudioModeChange: dubComposition.onAudioModeChange,
              busy: dubComposition.audioModeBusy,
              musicMode: dubComposition.musicMode,
              onMusicModeChange: dubComposition.onMusicModeChange,
              musicAvailable:
                musicSeparation.hasStems && !musicSeparation.noMusic,
              storyId: videoCreator.storyId ?? undefined,
              language: dubComposition.controlLanguage,
            }
          : undefined;
      const captionable = !!transcriptionStore.getOriginalTranscription();
      const playerAudioPickers =
        playerSettingsSlot && (captionable || dubAudio)
          ? createPortal(
              <PlayerAudioPickers
                captions={
                  captionable
                    ? {
                        videoCreator: adapter,
                        captionFallbackTemplate,
                        // The same list, names and preset test the review's
                        // picker gets, so the two menus are one.
                        templates: reviewCaptionTemplates,
                        resolveTemplateName: resolveCaptionTemplateName,
                        isPresetTemplate: isPresetCaptionTemplate,
                      }
                    : undefined
                }
                audio={dubAudio}
                menuZIndex={CLIP_PREVIEW_Z.modalPopover}
              />,
              playerSettingsSlot,
            )
          : null;

      return (
        <TranscriptContainer>
          <StoreContext.Provider value={clipRootProxy}>
            {playerAudioPickers}
            <ReelTranscriptStructureContext.Provider value={reelStructureView}>
              <TranscriptOverflowExtrasProvider>
                <Transcript
                  surface="content_studio_popup"
                  videoCreator={adapter}
                  onOpenClip={handleOpenSavedClip}
                  suspendAutoScroll={reviewOpen}
                  translationReviewOpen={reviewOpen}
                  saveService={saveService}
                  savePullQuoteService={savePullQuoteService}
                  clipSaveService={clipSaveService}
                  mediaService={mediaService}
                  hideAddPhoto={
                    !contentStudioTranscriptAddPhoto ||
                    enableTranslationReviewerMode
                  }
                  hideAddOverlayGraphic={
                    !enableTranscriptOverlayAdd ||
                    !enableCompositionCanvasQuickEdit ||
                    enableTranslationReviewerMode
                  }
                  onAddOverlayGraphicRequested={
                    handleAddOverlayGraphicRequested
                  }
                  // Translation reviewers get a read-only transcript: no structural
                  // edits (cut/remove/replace/save-as-clip) and no undo/redo. Inline
                  // translation span-replace + speaker editing stay on (they ride on
                  // enableTranslationReview / the context menu).
                  hideCutText={enableTranslationReviewerMode}
                  hideRemoveText={enableTranslationReviewerMode}
                  hideReplace={enableTranslationReviewerMode}
                  hideSaveAsClip={enableTranslationReviewerMode}
                  // Undo/redo is also hidden while a translated language is on
                  // screen: the undo stack doesn't cover translated edits, so the
                  // buttons only look like they work (flag defaults on).
                  showUndoRedo={
                    !enableTranslationReviewerMode &&
                    (contentStudioEnableTranslatedUndoRedo ||
                      isOriginalLang(selectedLanguage))
                  }
                  clipTimelineMarkers={timelineMarkers}
                  onMarkerClick={handleMarkerClick}
                  renderHighlightPopover={renderHighlightPopover}
                  onAdjustOverlayRange={
                    enableTranscriptOverlayRangeAdjust
                      ? handleAdjustOverlayRange
                      : undefined
                  }
                  suppressPlayShortcut
                  language={selectedLanguage}
                  enableInlineSpanReplace={enableTranslationReview}
                  // PLAT-10833 — Re-dub AI Voice in this clip transcript's own
                  // menu, scoped to the clip the way the review's entry is
                  // (lines this clip has no dubbed voice for are dropped).
                  // PLAT-10833 / PLAT-12047 / PLAT-12056 — Fix Translation and
                  // Re-dub AI Voice on this clip's prose hand off into the
                  // clip's review; the re-dub only when the clip has a dubbed
                  // voice.
                  reviewHandoff={reviewHandoff}
                  enableChangedHighlight={enableTranslationReview}
                  onCaptionsViewOpen={onLanguageChange}
                  // Rendered inside the clip's StoreContext provider above, so its
                  // hook resolves the CLIP's editor and karaoke store rather than
                  // the story-level ones.
                  belowHeader={
                    <>
                      {/* PLAT-12024 — lights up the words a re-dub just
                        landed on, inside this transcript's own DOM. Not
                        while the clip's review is open: the review lights
                        them in its own column, and a second pulse would
                        play "Show Passage" twice. */}
                      {videoCreator.storyId &&
                        selectedLanguage &&
                        !reviewOpen && (
                          <RedubLandedPulse
                            storyId={videoCreator.storyId}
                            language={selectedLanguage}
                            landed={redubLanded}
                          />
                        )}
                      <ClipVersionPreviewGuard adapter={adapter}>
                        {languagePlaces.title}
                      </ClipVersionPreviewGuard>
                      <CaptionStalenessNotice />
                      {/* What the reel adds up to, under the toolbar exactly as
                        the Reel Editor shows it: the prose below carries the
                        Chapter and Section headings, and this is the line at
                        reel scope above them. */}
                      {/* PLAT-11877 — the reel's chapters and sections belong to
                        the text; over caption lines they describe nothing. */}
                      {reelStructureView && !inCaptionsView && (
                        <ReelStructureSummaryLine
                          structure={reelStructureView.structure}
                          speakersByLabel={reelStructureView.speakersByLabel}
                        />
                      )}
                    </>
                  }
                  headerExtras={languagePlaces.toolbar}
                />
              </TranscriptOverflowExtrasProvider>
              {/* Guarded here, not around the Languages tab: the list is
                  portaled there, and its clicks follow this React tree. */}
              <ClipVersionPreviewGuard adapter={adapter}>
                {languagePlaces.panel}
              </ClipVersionPreviewGuard>
              {openMarker &&
                popoverFields.length > 0 &&
                // Portal to document.body for the same reasons as the hover
                // pill above — escapes the modal's transform-induced
                // stacking context so `position: fixed` is honoured against
                // the viewport and the popover paints above the canvas
                // iframe instead of being sized/positioned by an ancestor.
                createPortal(
                  <CompositionQuickEditPopover
                    // Remount per graphic. Clicking a different chip SWAPS the
                    // open marker without closing (see `handleMarkerClick`), and
                    // the rows below are keyed by control id — two Smart Graphics
                    // cards both key on `hook`, so without this React reuses the
                    // AI text control and carries its base-text scope, typed
                    // instructions and expanded state onto the next graphic.
                    key={openMarker.marker.id}
                    // Composition fields walk `elementPath` from the marker
                    // placement id; a synthesized text field (empty path) writes
                    // to its own resolved leaf id. See `resolveQuickEditTargetId`.
                    targetId={resolveQuickEditTargetId(
                      popoverFields,
                      openMarker.marker.id,
                    )}
                    headerLabel={formatHeaderLabelFromMarker(openMarker.marker)}
                    // PLAT-10066 — show the active caption language as its own
                    // top-row label (left of the close button) rather than a
                    // parenthetical on the name. `null` under original language,
                    // where the pencil edits the actual text (not a translation),
                    // so no language chip shows. Mirrors the canvas surface
                    // (`CompositionCanvasActionsLayer`).
                    activeLanguageLabel={activeLanguageDisplay}
                    anchorElement={openMarker.anchor}
                    fields={popoverFields}
                    // Original-text edits flow through the editor's command
                    // system (`commandUpdateElement` → `UpdateElementCommand`
                    // → `RendererSyncService`), matching the Creator Studio
                    // side-panel text-control path. Earlier versions wrote
                    // directly to `VideoSourceStore.setSource` which left the
                    // editor unaware and the iframe never repainted.
                    editorStore={editorStore}
                    // PLAT-10046 — translated-row + footer-action support.
                    // `activeLanguage` switches the row shape (read-only
                    // original + translated pair under non-original);
                    // `compositionPhrases` drives badge + button visibility;
                    // `retranslateCompositionPhrases` powers the Translate /
                    // Retranslate buttons (wraps the store method with the
                    // live `compositionTargets` + `lang`).
                    activeLanguage={activeLangForPopover}
                    compositionPhrases={compositionPhrases}
                    retranslateCompositionPhrases={
                      retranslateCompositionDispatch
                    }
                    // PLAT-10046 — manual translated-value pin write +
                    // source-language label for the disclosure.
                    upsertCompositionPhraseTranslation={
                      upsertCompositionPhraseDispatch
                    }
                    removeCompositionPhrasePin={
                      removeCompositionPhrasePinDispatch
                    }
                    sourceLanguage={transcriptionStore.language}
                    // PLAT-10046 analytics context — fires translate /
                    // retranslate clicked + failed events from the popover.
                    storyId={videoCreator.storyId ?? null}
                    compositionType={openMarker.marker.type}
                    surface="content-studio-clip-preview"
                    timing={{
                      time: openMarker.marker.time,
                      endTime: openMarker.marker.endTime,
                    }}
                    framerate={adapter.getSourceVideoFrameRate()}
                    // PLAT-10913 — the AI text control resolves its base text
                    // from the same marker + clip transcript the PLAT-10045
                    // underline uses, so the words it previews are the words
                    // underlined for this graphic (E4 / DS3). The wiring is
                    // shared with the canvas Edit pill so the two entry points
                    // can't drift.
                    aiTextContext={buildAITextPopoverContext({
                      aiTextGeneration,
                      transcriptElements: clipTranscriptElements,
                      overlay: openMarker.marker,
                    })}
                    onCommitTiming={handleCommitOverlayTiming}
                    onSaved={handleSaved}
                    onClose={closePopover}
                  />,
                  document.body,
                )}
              {generateModal.isOpen && (
                <TranslationGenerateModal
                  {...generateModal.modalProps}
                  onSuccess={language => {
                    onLanguagePick(language);
                    generateModal.close();
                  }}
                  // PLAT-11574: the "ready" toast opens the review workspace
                  // for the translated language (switching the transcript to
                  // it first, like the row action).
                  successAction={
                    enableTranslationReview
                      ? language => ({
                          label: 'Review Translation',
                          onClick: () => {
                            onLanguagePick(language);
                            setReviewOpenAndUrl(true, 'translation');
                          },
                        })
                      : undefined
                  }
                  // PLAT-11877 — and a link to that same review: the params
                  // the two writes above would leave in the URL, so the link
                  // restores exactly what Review Translation opens.
                  successSecondaryAction={
                    enableTranslationReview
                      ? language => {
                          const params = new URLSearchParams(searchParams);
                          params.set('clipLang', language);
                          params.set(CLIP_REVIEW_PARAM, reviewViewMode);
                          params.set(CLIP_REVIEW_MODE_PARAM, 'translation');
                          return copyReviewLinkToastAction({
                            href: reviewLinkFromParams(params),
                            showToast: toast => videoCreator.showToast(toast),
                            analytics: {
                              story_id: videoCreator.storyId ?? undefined,
                              language,
                              entry: 'clip',
                              view_mode: reviewViewMode,
                            },
                          });
                        }
                      : undefined
                  }
                  supportedLanguages={supportedLanguages}
                />
              )}
              {redubSelection.current && videoCreator.storyId && (
                <RedubSelectionPopover
                  storyId={videoCreator.storyId}
                  targetLanguage={redubSelection.current.language}
                  supportedLanguages={supportedLanguages}
                  request={redubSelection.current.request}
                  // The same room the gap-fill modal below hands the engine:
                  // the source video's whole length, on the STORY's timeline.
                  availableDurationMs={
                    videoCreator.story?.originalVideo?.video?.duration
                      ? Math.round(
                          videoCreator.story.originalVideo.video.duration *
                            1000,
                        )
                      : undefined
                  }
                  onClose={redubSelection.close}
                  onSuccess={result => {
                    // The manifest changed on the server and nothing here
                    // observes that, so re-read it, then rebuild the clip
                    // so the new take is audible without a reload.
                    setDubCoverageRefresh(token => token + 1);
                    dubComposition.reapplyComposition?.();
                    // PLAT-12024 — and show which words changed.
                    landRedub(result.coverage.synthesized);
                  }}
                  onRevealPassage={revealRedub}
                />
              )}
              {fillGapsModal.isOpen &&
                fillGapsModal.targetLanguage &&
                videoCreator.storyId && (
                  <DubGenerateModal
                    storyId={videoCreator.storyId}
                    targetLanguage={fillGapsModal.targetLanguage}
                    supportedLanguages={supportedLanguages}
                    segmentIds={fillGapsModal.segmentIds ?? undefined}
                    fillGaps={fillGapsModal.isFillGaps}
                    firstDub={fillGapsModal.isFirstDub}
                    music={keepOriginalMusic}
                    /* Hand the engine the room the dub really has: the source
                      video's whole length. Its own default ceiling is the last
                      spoken word, which a translation always overruns, so
                      without this every gap fill re-records all of the story's
                      lines. Milliseconds on the STORY's timeline, never this
                      clip's length. */
                    availableDurationMs={
                      videoCreator.story?.originalVideo?.video?.duration
                        ? Math.round(
                            videoCreator.story.originalVideo.video.duration *
                              1000,
                          )
                        : undefined
                    }
                    onClose={fillGapsModal.close}
                    // PLAT-11662: the "ready" toast opens Dubbing Review for
                    // the dubbed language (the counterpart of the translation
                    // modal's "Review Translation").
                    successAction={
                      enableTranslationReview
                        ? language => ({
                            label: 'Review Dub',
                            onClick: () => {
                              onLanguagePick(language);
                              setReviewOpenAndUrl(true, 'dubbing');
                            },
                          })
                        : undefined
                    }
                    onSuccess={() => {
                      // The manifest changed on the server and nothing here
                      // observes that, so re-read it.
                      setDubCoverageRefresh(token => token + 1);
                      if (fillGapsModal.isFirstDub) {
                        // PLAT-11440 — a FIRST dub is the one run that creates a
                        // dub this story did not have, and two things on this
                        // screen were written for runs that repair one.
                        //
                        // The story's dubbed-language list is read once when the
                        // surface opens, so without a re-read the Voice toggle
                        // never appears and the button goes on offering a dub
                        // that now exists.
                        //
                        // And the clip is still on Original, which is exactly
                        // the state that makes `reapplyComposition` do nothing.
                        // Putting the clip on the dub it just made is what makes
                        // the new voice audible without a reload; the language
                        // is the one the button named, because the clip carries
                        // no dub of its own to be confused with.
                        dubComposition.refreshDubbedLanguages?.();
                        dubComposition.onAudioModeChange?.('dubbed');
                        return;
                      }
                      // Otherwise rebuild the clip itself. Re-reading the
                      // warning alone said the gap was filled while the clip
                      // went on playing silence over it, because its audio
                      // elements were still the ones built from the old
                      // manifest. Neither position moves: this is the same dub,
                      // with the lines just asked for in it.
                      dubComposition.reapplyComposition?.();
                    }}
                  />
                )}
              {removeDub.pendingLanguage && (
                <ConfirmModal
                  closeModal={removeDub.cancel}
                  onCancel={removeDub.cancel}
                  onConfirm={removeDub.confirm}
                  // Says whose dub it is: from a clip, "the story's" is the
                  // part a person could not guess.
                  title={`Remove the story's ${captionForLanguage(
                    removeDub.pendingLanguage,
                  )} dub?`}
                  confirmText="Remove dub"
                  cancelText="Cancel"
                />
              )}
              {/* T3.3 (PLAT-10286) — Translation Review scoped to THIS clip.
              Mounted inside the clip proxy so the panes + player drive off the
              clip-scoped `adapter` (clip transcript range; the player plays
              the original story video bounded to `timeRange`). Raised above
              the clip-preview stack (CLIP_PREVIEW_Z) so the full-surface
              modal covers the preview it was launched from. */}
              {reviewOpen && selectedLanguage && (
                <TranslationReviewWorkspace
                  language={selectedLanguage}
                  videoCreator={adapter}
                  supportedLanguages={supportedLanguages}
                  // Must be this surface's own key. Was `translationView`,
                  // owned by the always-mounted Transcript tab, which rewrites the
                  // whole query string and deleted it on the next render.
                  initialViewMode={reviewViewMode}
                  onViewModeChange={setReviewViewModeAndUrl}
                  initialReviewMode={reviewMode ?? undefined}
                  // PLAT-11662 — only the job is mirrored here; the panel and
                  // reference column are not URL-addressable on this entry.
                  onViewStateChange={state => {
                    if (state.reviewMode !== reviewMode) {
                      setReviewModeAndUrl(state.reviewMode);
                    }
                  }}
                  zoomLevel={0}
                  zIndex={CLIP_PREVIEW_Z.translationReview}
                  // PLAT-11656 — the same page beside the sidebar as the story
                  // entry; `liftOverlays` lifts the sidebar above the clip
                  // preview's scrim, so it stays crisp and clickable.
                  layout="page"
                  timeRange={reviewTimeRange}
                  poster={reviewPoster}
                  // PLAT-12024 — the review lights the landed lines in its own
                  // column, as the clip's transcript does.
                  landedRedub={redubLanded}
                  liftOverlays
                  story={story}
                  sessionStore={sessionStore}
                  clip={clip}
                  clipKind={currentIsReel ? 'Reel' : 'Clip'}
                  // PLAT-11340 — the review's Original / Dubbed / Both control,
                  // for a language the story has a dub in. The review's own
                  // renderer composites the dub (the workspace keeps its
                  // browser-audio overlay off on this entry), so this no
                  // longer risks a second copy of the dub over the clip.
                  // Seeded from the clip's own position when the clip already
                  // plays this language's dub, so the review opens hearing
                  // what the preview was playing; otherwise from Original.
                  enableDubMonitor={
                    !!dubComposition.onAudioModeChange &&
                    dubComposition.dubbedLanguages.includes(selectedLanguage)
                  }
                  // PLAT-11685 — the first dub, offered from inside Dubbing
                  // Review when the reviewed language has none. The CLIP's own
                  // dub, matching this surface's Dub button (PLAT-11440) rather
                  // than the whole story's. `handleDubThisClip` returns silently
                  // when the clip is over the duration cap or has nothing to
                  // dub — the toggle paints its button inert with the reason,
                  // which the review has no room for, so the action is simply
                  // not offered rather than offered and dead.
                  onRequestDub={
                    dubComposition.onAudioModeChange &&
                    !clipDubUnavailableNote &&
                    selectedGapPhraseIds.length > 0
                      ? handleDubThisClip
                      : undefined
                  }
                  // PLAT-11381 — this entry reviews ONE clip's slice of the
                  // story's transcript. Said outright rather than left for the
                  // review to infer from which optional props arrived.
                  transcriptScope="clip"
                  // PLAT-11382 — re-dub the lines the reviewer selected, using
                  // the same popover this surface already opens to re-record
                  // the clip's lines whose words were edited after they were
                  // dubbed. `scopeRedubToClip` narrows the selection to what
                  // this clip actually has a recorded voice for.
                  onRequestReDubSelection={
                    dubComposition.onAudioModeChange
                      ? handleReDubReviewSelection
                      : undefined
                  }
                  // PLAT-12047 — the passage this clip's menu handed over.
                  pendingHandoff={pendingHandoff}
                  onHandoffConsumed={() => setPendingHandoff(null)}
                  initialAudioMode={
                    dubComposition.appliedDubLanguage === selectedLanguage
                      ? dubComposition.audioMode
                      : 'original'
                  }
                  dubMusicBedUrl={musicSeparation.bedUrl}
                  dubVoiceStemUrl={
                    musicSeparation.voiceUrlMp3 ?? musicSeparation.voiceUrlWav
                  }
                  // PLAT-11665 — the review's Music picker starts where the
                  // clip has its music.
                  initialMusicMode={dubComposition.musicMode}
                  // PLAT-11899 / PLAT-11927 — the review's levels start at the
                  // clip's, and its Voice, Music and applied levels are kept
                  // on the clip through THIS surface's editor, the one the
                  // clip is saved from.
                  clipVoiceVolume={dubComposition.voiceVolume}
                  clipOriginalVolume={dubComposition.originalVolume}
                  clipSegmentLevels={dubComposition.segmentLevels}
                  clipLeadIn={dubComposition.leadIn}
                  onApplyToClip={dubComposition.onReviewSave}
                  captionFallbackTemplate={reviewCaptionFallbackTemplate}
                  captionTemplates={reviewCaptionTemplates}
                  resolveCaptionTemplateName={resolveCaptionTemplateName}
                  isPresetCaptionTemplate={isPresetCaptionTemplate}
                  onClose={() => setReviewOpenAndUrl(false)}
                />
              )}
            </ReelTranscriptStructureContext.Provider>
          </StoreContext.Provider>
          {/* PLAT-10105 — overlay picker lives OUTSIDE the clip proxy so the
            CompositionProvider reads the real org/template stores (the same
            set the graphics screen shows). The Modal itself portals to
            document.body. */}
          {overlaySelection && (
            <CompositionProvider>
              <OverlayGraphicPickerModal
                onSelect={handleSelectOverlay}
                onClose={() => setOverlaySelection(null)}
                currentAspectRatio={adapter.getCurrentAspectRatio()}
              />
            </CompositionProvider>
          )}
          {/* PLAT-11257 — the ask a generated name owes a typed one (DE10), and
              where the creator says what the name should say. The Reel Editor's
              own dialog: the same question deserves the same box, whichever tab
              it was asked from. */}
          {nameDialogTarget && (
            <ReelNameGenerationDialog
              kind="section"
              // The creator opened this box themselves, from the menu item that
              // says so — it is where they steer the run, not a warning.
              intent="guide"
              currentName={nameDialogTarget.currentName}
              onClose={() => setNameDialogTarget(null)}
              onGenerate={guidance => {
                const target = nameDialogTarget;
                setNameDialogTarget(null);
                void generateSectionName(target.id, {
                  userNameConfirmed: true,
                  guidance,
                });
              }}
            />
          )}
        </TranscriptContainer>
      );
    },
  );

/**
 * Outer component — accepts nullable props (as provided by useClipPreviewRenderer)
 * and renders a loading state until both editorStore and adapter are ready.
 */
export const ClipTranscriptBridge: React.FC<ClipTranscriptBridgeProps> =
  observer(
    ({
      editorStore,
      adapter,
      selectedLanguage,
      onSelectedLanguageChange,
      isVisible,
      languagesPane,
      onOpenLanguages,
      onCloseLanguages,
      story,
      sessionStore,
      clip,
      onOpenSectionInReelEditor,
    }) => {
      const [searchParams] = useSearchParams();
      // PLAT-11037 — a shared review link must not flash the clip preview behind
      // it. The loaded bridge below seeds the review synchronously from the same
      // param, so it opens in the SAME commit that bridge mounts — but that only
      // happens once `useClipPreviewRenderer` has booted the clip-scoped editor +
      // adapter, and the preview modal is already painted by then. Veil that
      // window with the workspace's own backdrop.
      //
      // Shares `isClipReviewRequested` with that seed deliberately: gating the
      // veil on its own copy of the predicate is how it silently stopped firing.
      //
      // Captured once on mount, so it only ever covers the deep-link restore —
      // never a later editor re-init (e.g. an aspect-ratio switch) while the
      // reviewer has the review open and is working.
      const [pendingReviewRestore] = useState(() =>
        isClipReviewRequested(searchParams),
      );

      if (!editorStore || !adapter) {
        return (
          <>
            <LoadingContainer>
              <LoadingState message="Loading transcript…" />
            </LoadingContainer>
            {pendingReviewRestore && (
              <TranslationReviewRestoreVeil
                zIndex={CLIP_PREVIEW_Z.translationReview}
                layout="page"
              />
            )}
          </>
        );
      }

      return (
        <ClipTranscriptBridgeLoaded
          editorStore={editorStore}
          adapter={adapter}
          selectedLanguage={selectedLanguage}
          onSelectedLanguageChange={onSelectedLanguageChange}
          isVisible={isVisible}
          languagesPane={languagesPane}
          onOpenLanguages={onOpenLanguages}
          onCloseLanguages={onCloseLanguages}
          story={story}
          sessionStore={sessionStore}
          clip={clip}
          onOpenSectionInReelEditor={onOpenSectionInReelEditor}
        />
      );
    },
  );

/**
 * PLAT-10045 — wrapper for the transcript-hover canvas-style action pill.
 * (left, top) inline style is the desired CENTER anchor point;
 * `translate(-50%, -50%)` self-centers the pill on it. Matches the
 * positioning convention used by `MenuPosition` inside
 * `CompositionCanvasActionsLayer` so the two surfaces share a behaviour.
 */
const TranscriptHoverMenuPosition = styled.div`
  position: fixed;
  pointer-events: none;
  transform: translate(-50%, -50%);
  z-index: ${CLIP_PREVIEW_Z.transcriptHoverPill};

  & > * {
    pointer-events: auto;
  }
`;

const TranscriptContainer = styled.div`
  /* The Languages panel covers this pane (PLAT-11877). */
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Remove Transcript's Main margin-left (causes overflow in the modal's
     constrained right pane) and prevent any horizontal shifting during playback.
     Also drop Main's 12px panel padding-top: the modal's tab header already
     provides the breathing room, and the extra padding pushed the transcript
     toolbar below the left pane's control-bar row (PLAT-10646). */
  > div {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    padding-top: 0;
  }

  /* Align transcript text with the header controls row across all views
     (regular, redline, captions). The Buttons row uses padding-left: 6px;
     match that so speaker labels and text start at the same left position. */
  #transcript-scrollable-container {
    padding-left: 6px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
`;
